import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  TableHead,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  IconButton,
  Badge,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parse, isValid, subWeeks, isSaturday } from 'date-fns';
import { chartDataService } from '../services/chartDataService';
import { backendService } from '../services/backendService';
import { CreateSheetDialog } from './CreateSheetDialog';
import { UrlDialog } from './UrlDialog';
import { useNavigate } from 'react-router-dom';
import { ChartUploadDialog } from './ChartUploadDialog';
import { AddNewVideoDialog } from '../components/AddNewVideoDialog';
import { processChartText } from '../services/chartProcessingService';
import { AddVideoDetailsDialog } from '../components/AddVideoDetailsDialog';
import { YouTubeSearchDialog } from './YouTubeSearchDialog';
import { youtubeService } from '../services/youtubeService';
import { SeekTimeDialog } from './SeekTimeDialog';
import { PickersDay, PickersDayProps, DateView } from '@mui/x-date-pickers';
import { normalizeSongArtistKey, normalizeSongTitle } from '../utils/stringNormalization';
import { addLineBreakDelimiter, splitAtDelimiter, delimiterToNewline, newlineToDelimiter } from '../utils/textSplitting';
import { ReorderDialog } from './ReorderDialog';
import { AuthContext } from '../contexts/AuthContext'; // Import AuthContext instead of useAuth
import { LoginIndicator } from './LoginIndicator';
import { useThemeColors } from '../theme/useThemeColors';
import { useGoogleSheets } from '../context/GoogleSheetsContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { DriveFile } from '../types/google-api'; // Import DriveFile type
import { useSnackbar } from 'notistack'; // Added for notifications
import { SERVER_BASE_URL, getVoLanguage } from '../config';

// Canonical weekly-chart column layout (matches chartDataService.getWeek / the
// server's WEEKLY_HEADERS). The grid renders from getWeek's { values }, so a
// 0-based data column index maps deterministically to a DB domain field. Used
// to convert grid-cell edits into typed chartDataService.setPositionField calls
// (no A1 notation). Columns not listed (VO L-P/S/V, Position A, Adjustment R,
// blank T/U) are not edited from this grid.
const COLUMN_FIELD_MAP: { [colIndex: number]: { field: string } } = {
  1: { field: 'song' },        // B
  2: { field: 'artist' },      // C
  3: { field: 'lastWeek' },    // D
  4: { field: 'peak' },        // E
  5: { field: 'weeks' },       // F
  6: { field: 's3Url' },       // G (video framing)
  7: { field: 'inPoint' },     // H (video framing)
  8: { field: 'duration' },    // I (video framing)
  9: { field: 'zoom' },        // J (video framing)
  10: { field: 'message' },    // K
  16: { field: 'verticalUrl' },// Q (video framing)
};
// Rows from getWeek are ordered position DESC (row 0 = position 10 … row 9 =
// position 1). data.rows is 0-based, so position = 10 - rowIndex. (Equivalently
// the old sheet rowNumber was rowIndex + 2, and position = 12 - rowNumber.)
const positionForRowIndex = (rowIndex: number): number => 10 - rowIndex;

// Add interface for change history
interface ChangeHistory {
  row: number;
  col: number;
  oldValue: string;
  newValue: string;
  range: string;
}

interface Video {
  song: string;
  artist: string;
  youtubeLink?: string;
}

interface SpreadsheetViewerProps {
  spreadsheetId?: string;  // DEPRECATED: Use context instead. This is actually the folder ID
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  setChartDate?: React.Dispatch<React.SetStateAction<string>>;
}

interface VideoManagerData {
  song?: string;
  artist?: string;
  youtubeLink?: string;
  voiceOverText?: string;
}

// Add data type interface between other interfaces
interface SpreadsheetData {
  headers: string[];
  rows: string[][];
  originalHeaders?: string[];
  originalRows?: string[][];
}

// Add helper function to extract YouTube video ID
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// Update the RenderDialog component with proper typing
const RenderDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  spreadsheetId: string | null; // Keep spreadsheetId
  voUrlColumnIndex: number | null; // Add the original column index
  voInPoints: { [key: string]: string };
  voOutPoints: { [key: string]: string };
}> = ({ open, onClose, spreadsheetId, voUrlColumnIndex, voInPoints, voOutPoints }): React.ReactElement => { // <-- Add return type here
  // State to hold the fetched URL
  const [recentVoUrl, setRecentVoUrl] = useState<string>('');
  const [loadingUrl, setLoadingUrl] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVoUrl = async () => {
      // Only fetch if dialog is open, we have ID, and a valid column index
      if (!open || !spreadsheetId || voUrlColumnIndex === null || voUrlColumnIndex < 0) {
        setRecentVoUrl('');
        setFetchError(null);
        setLoadingUrl(false); // Ensure loading is stopped
        if (open && spreadsheetId && voUrlColumnIndex === null) {
           console.warn('[RenderDialog] Cannot fetch VO URL: voUrlColumnIndex is null.');
           // Optionally set an error message here
        }
        return;
      }

      setLoadingUrl(true);
      setFetchError(null);
      setRecentVoUrl('');

      try {
        // Most-recent processed VO URL for the week (week-level field in the DB).
        console.log(`[RenderDialog] Fetching processed VO URL for ${spreadsheetId}`);
        const vo = await chartDataService.getVO(spreadsheetId, getVoLanguage(spreadsheetId));
        const url = String(vo.processedVoUrl || '').trim();
        console.log(`[RenderDialog] Fetched processed VO URL: "${url}"`);
        setRecentVoUrl(url);
      } catch (error) {
        console.error('[RenderDialog] Error fetching VO URL:', error);
        setFetchError('Failed to fetch VO URL.');
        setRecentVoUrl('');
      } finally {
        setLoadingUrl(false);
      }
    };

    fetchVoUrl();
  }, [open, spreadsheetId, voUrlColumnIndex]); // Add voUrlColumnIndex dependency


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>VO Render Preview</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Most Recent VO File: {loadingUrl ? <CircularProgress size={16} /> : fetchError || recentVoUrl || 'None'}
        </Typography>
        {fetchError && <Alert severity="error">{fetchError}</Alert>}
        <List>
          {['Intro', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1', 'Outro'].map((key) => (
            <ListItem key={key}>
              <ListItemText
                primary={key}
                secondary={`In: ${voInPoints[key] || '-'} | Out: ${voOutPoints[key] || '-'}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  spreadsheetId: propFolderId,
  selectedDate,
  onDateChange,
  setChartDate,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useContext(AuthContext);

  // Chart category + its folder id from context. folderId is no longer used for
  // any data access (the app is Google-Sheets-free); it remains the gate for
  // initialization and detecting a chart-category switch.
  const { chartCategoryFolderId, chartCategory, isLoading: isContextLoading } = useGoogleSheets();
  const { themeColor, themeHoverColor } = useThemeColors();
  const folderId = chartCategoryFolderId || propFolderId || '';
  const [data, setData] = useState<SpreadsheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUrl, setEditingUrl] = useState<{ row: number; col: number; url: string } | null>(null);
  const [editingS3Url, setEditingS3Url] = useState<{ row: number; col: number; url: string } | null>(null);
  const [uploadChartDialogOpen, setUploadChartDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchTargetRowIndex, setSearchTargetRowIndex] = useState<number | null>(null);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');
  const [videoData, setVideoData] = useState<{ [key: string]: VideoManagerData }>({});
  const [changeHistory, setChangeHistory] = useState<ChangeHistory[]>([]);
  const [redoHistory, setRedoHistory] = useState<ChangeHistory[]>([]);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [sortAscending, setSortAscending] = useState(true);
  const [songArtistSuggestions, setSongArtistSuggestions] = useState<string[]>([]);
  const [artistSuggestions, setArtistSuggestions] = useState<string[]>([]);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [addNewVideoDialogOpen, setAddNewVideoDialogOpen] = useState(false);
  const [newVideosToAdd, setNewVideosToAdd] = useState<{song: string, artist: string}[]>([]);
  const [uploadedChartText, setUploadedChartText] = useState<string>('');
  const [addVideoDialogOpen, setAddVideoDialogOpen] = useState(false);
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [voUrlColumnIndex, setVoUrlColumnIndex] = useState<number | null>(null);
  const [importManagerNotifications, setImportManagerNotifications] = useState<number>(0);
  const [outputManagerNotifications, setOutputManagerNotifications] = useState<number>(0);
  const [voSongColIndex, setVoSongColIndex] = useState<number | null>(null);
  const [voInPointColIndex, setVoInPointColIndex] = useState<number | null>(null);
  const [voOutPointColIndex, setVoOutPointColIndex] = useState<number | null>(null);
  const [voPositionColIndex, setVoPositionColIndex] = useState<number | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number; value: string } | null>(null);
  
  // State for SeekTimeDialog
  const [seekTimeDialogOpen, setSeekTimeDialogOpen] = useState(false);
  const [seekTimeDialogData, setSeekTimeDialogData] = useState<{
    row: number;
    col: number;
    videoUrl: string;
    songTitle: string;
    artistName: string;
    currentSeekTime: number;
  } | null>(null);

  // Add handler to toggle sort direction
  const toggleSortDirection = () => {
    setSortAscending(prev => !prev);
  };


  // Fetch Import Manager notification count (only when tab is visible)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const fetchImportManagerNotifications = async () => {
      try {
        const response = await fetch(`${SERVER_BASE_URL}/api/import-manager/jobs?status=crop_needed`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setImportManagerNotifications(data.count || 0);
          }
        }
      } catch (error) {
        console.error('[Import Manager Notifications] Error fetching count:', error);
      }
    };

    const startPolling = () => {
      fetchImportManagerNotifications();
      interval = setInterval(fetchImportManagerNotifications, 10000);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (!document.hidden) {
      startPolling();
    }

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch Output Manager notification count (activity-based - only during exports)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let stopTimeout: NodeJS.Timeout | null = null;
    let isExportActive = false;
    const mountTime = new Date().toISOString();

    console.log(`[Output Manager Notifications] useEffect MOUNTED at ${mountTime}`);

    const fetchOutputManagerNotifications = async (reason: string) => {
      console.log(`[Output Manager Notifications] FETCHING - reason: ${reason}, isExportActive: ${isExportActive}, hasInterval: ${!!interval}`);
      try {
        const response = await fetch(`${SERVER_BASE_URL}/api/get-recent-outputs`);
        if (response.ok) {
          const data = await response.json();
          // Count outputs that are unviewed (still in PostgreSQL, not yet moved to Render History)
          const unviewedCount = Array.isArray(data) ? data.filter((output: any) => output.viewed === false).length : 0;
          setOutputManagerNotifications(unviewedCount);
        }
      } catch (error) {
        console.error('[Output Manager Notifications] Error fetching count:', error);
      }
    };

    const startPolling = () => {
      if (interval) return; // Already polling
      console.log('[Output Manager Notifications] Starting polling (export active)');
      fetchOutputManagerNotifications('polling-start');
      interval = setInterval(() => fetchOutputManagerNotifications('polling-interval'), 10000);
    };

    const stopPolling = () => {
      if (interval) {
        console.log('[Output Manager Notifications] Stopping polling');
        clearInterval(interval);
        interval = null;
      }
    };

    const handleExportPollingChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ isPolling: boolean }>;
      const wasActive = isExportActive;
      isExportActive = customEvent.detail.isPolling;

      console.log(`[Output Manager Notifications] EVENT received: isPolling=${isExportActive} (was ${wasActive})`);

      // Clear any pending stop timeout
      if (stopTimeout) {
        clearTimeout(stopTimeout);
        stopTimeout = null;
      }

      if (isExportActive && !document.hidden) {
        startPolling();
      } else if (!isExportActive) {
        // Continue polling for 30 seconds after export stops to catch late updates
        console.log('[Output Manager Notifications] Export stopped, polling for 30 more seconds');
        stopTimeout = setTimeout(() => {
          console.log('[Output Manager Notifications] 30 second buffer expired, stopping polling');
          stopPolling();
        }, 30000);
      }
    };

    const handleVisibilityChange = () => {
      console.log(`[Output Manager Notifications] Visibility changed: hidden=${document.hidden}, isExportActive=${isExportActive}`);
      if (document.hidden) {
        stopPolling();
      } else if (isExportActive) {
        startPolling();
      }
    };

    // Fetch once on mount (badge is accurate when app opens)
    fetchOutputManagerNotifications('initial-mount');

    // Listen for export activity from OutputManager
    window.addEventListener('exportPollingChange', handleExportPollingChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log(`[Output Manager Notifications] useEffect UNMOUNTING (was mounted at ${mountTime})`);
      stopPolling();
      if (stopTimeout) clearTimeout(stopTimeout);
      window.removeEventListener('exportPollingChange', handleExportPollingChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // First, memoize the getFormattedDate function
  const getFormattedDate = useCallback((date: Date): string => {
    return format(date, 'M-d-yy');
  }, []);

  // Weekly-sheet listing — lists weeks from the Postgres chart-data API keyed by
  // chart name. The returned {id,name} shape (id = source_sheet_id, the existing
  // spreadsheetId key) is what all downstream date-parsing/selection logic uses.
  const listWeeklyFiles = useCallback(async (): Promise<{ id: string; name: string }[]> => {
    if (!chartCategory) return [];
    return chartDataService.listWeeks(chartCategory);
  }, [chartCategory]);

  // Resolve a weekly sheet's display name from the weeks list. NEVER throws —
  // the name is only used as navigation-state context, so an empty string is an
  // acceptable fallback when the id isn't found (or the list fetch fails).
  const resolveWeeklySheetName = useCallback(async (sheetId: string): Promise<string> => {
    try {
      const files = await listWeeklyFiles();
      const match = files.find(f => f.id === sheetId);
      if (match?.name) return match.name;
    } catch (e) {
      console.warn('[SpreadsheetViewer] resolveWeeklySheetName: weeks-list lookup failed:', e);
    }
    return '';
  }, [listWeeklyFiles]);

  // Memoize the formatChartDate function
  const formatChartDate = useCallback((date: Date): string => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }, []);

  const CustomDay = useCallback((props: any) => {
    const { day, ...other } = props;
    const isDaySaturday = isSaturday(day);
    const formattedDay = getFormattedDate(day);
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDay
    );

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          color: isDaySaturday ? (isAvailable ? 'black' : 'gray') : 'gray',
          '&.Mui-selected': {
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: 'black'
            }
          },
          '&:hover': {
            backgroundColor: isDaySaturday ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
          }
        }}
      >
        {isDaySaturday && !isAvailable ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: 'gray'
            }}
          >
            +
          </Box>
        ) : (
          day.getDate()
        )}
      </PickersDay>
    );
  }, [availableDates, getFormattedDate]);

  // Now wrap loadData in useCallback to prevent it from changing on every render
  // Optional spreadsheetId parameter allows passing the ID directly to avoid stale state issues
  const loadData = useCallback(async (sheetName: string, spreadsheetId?: string): Promise<SpreadsheetData | null> => {
    try {
      const targetSpreadsheet = spreadsheetId || selectedSpreadsheet;
      if (!targetSpreadsheet) {
        // Benign: fires during init before a sheet is selected. Use log (not
        // error) so it's silenced in production.
        console.log('[loadData] No spreadsheet selected');
        return null;
      }

      console.log(`[loadData] Loading data from spreadsheet ${targetSpreadsheet}, sheet ${sheetName}...`);

      setLoading(true);
      // Get the weekly chart table, reconstructed from Postgres (chart_positions
      // + videos + vo_lines) in the { headers, rows } shape the grid renders.
      // This is what lets DB-created and imported weeks display.
      const result = await chartDataService.getWeek(targetSpreadsheet, getVoLanguage(targetSpreadsheet));

      if (!result.values || result.values.length === 0) {
        console.error('[loadData] No data found in spreadsheet');
        setError('No data found in spreadsheet');
        return null;
      }
      
      console.log(`[loadData] Loaded ${result.values.length} rows`);
      
      // Get header row first
      const headers = result.values[0];
      
      // Get all other rows (data rows)
      const rows = result.values.slice(1);

      const newData: SpreadsheetData = {
        headers,
        rows,
        originalHeaders: [...headers],
        originalRows: rows.map(row => [...row])
      };

      console.log(`[loadData] Processed data with ${headers.length} columns and ${rows.length} rows`);
      console.log(`[loadData] Sample headers:`, headers.slice(0, 5));
      console.log(`[loadData] Sample first row:`, rows[0]?.slice(0, 5));

      // The chart date is the week's date_string in the DB; it's set by the
      // caller (handleDateChange / handleCreateSheet) when a week is selected or
      // created, so loadData no longer derives it from a filename or writes it back.

      // Process and save the data. Positions already carry their linked video
      // data (joined server-side in getWeek), so there's no master-sync step.
      console.log(`[loadData] Setting data with headers length:`, newData.headers.length, 'rows length:', newData.rows.length);
      setData(newData);
      generateAutocompleteOptions(newData);

      // Clear any previous errors
      setError(null);

      setLoading(false);
      return newData;
    } catch (err) {
      console.error('[loadData] Error loading data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      setLoading(false);
      return null;
    }
  }, [selectedSpreadsheet, setData, setError, setLoading]); // Specify all dependencies

  const loadChartData = async () => {
    try {
      console.log('[loadChartData] Starting chart data load...');
      if (!selectedSpreadsheet) {
        // Benign init-time guard; log (not error) so it's silenced in production.
        console.log('[loadChartData] No spreadsheet selected');
        return;
      }

      await loadData('Sheet1');

    } catch (err) {
      console.error('[loadChartData] Error loading chart data:', err);
    }
  };

  const handleDateChange = async (newDate: Date | null) => {
    if (!newDate || !isValid(newDate)) {
      console.log('[handleDateChange] Invalid date selected:', newDate);
      setError('Invalid date selected');
      return;
    }
    
    const formattedDate = getFormattedDate(newDate);
    console.log('[handleDateChange] Processing date:', formattedDate);
    
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDate
    );
    console.log('[handleDateChange] Is date available:', isAvailable);
    
    try {
      setLoading(true);
      setError(null);
      
      const files = await listWeeklyFiles();
      console.log('[handleDateChange] Available files:', files);
      
      const matchingSpreadsheet = files.find(file => file.name === formattedDate);
      console.log('[handleDateChange] Matching spreadsheet:', matchingSpreadsheet);
      
      if (matchingSpreadsheet) {
        console.log('[handleDateChange] Loading existing spreadsheet:', matchingSpreadsheet.id);
        setSelectedSpreadsheet(matchingSpreadsheet.id);
        localStorage.setItem(`billboard_currentSpreadsheetId_${chartCategory}`, matchingSpreadsheet.id);
        onDateChange(newDate);

        // Skip the LoadDataEffect since we're manually loading here
        skipNextLoadDataEffect.current = true;
        // Load the data - pass spreadsheet ID directly to avoid stale state race condition
        await loadData('Sheet1', matchingSpreadsheet.id);

        // Update the app title from the selected date. The week's stored
        // date_string lives in the DB (set at create time); nothing to write here.
        const readableDate = formatChartDate(newDate);
        if (setChartDate) {
          console.log(`[handleDateChange] Updating chart date in App title: ${readableDate}`);
          setChartDate(readableDate);
        }
      } else if (isSaturday(newDate)) {
        console.log('[handleDateChange] Creating new week for Saturday');

        // Create the week server-side. This carries over the previous week's
        // settings + song positions and inserts no VO lines (the relational
        // equivalent of "duplicate the previous sheet + clear all VO cells").
        const created = await chartDataService.createWeek(chartCategory, formattedDate, formatChartDate(newDate));
        const newSpreadsheetId = { id: created.id };
        console.log('[handleDateChange] Created new week in DB:', created.id);

        setSelectedSpreadsheet(newSpreadsheetId.id);
        localStorage.setItem(`billboard_currentSpreadsheetId_${chartCategory}`, newSpreadsheetId.id);
        onDateChange(newDate);

        // Update the app title. The week's date_string was set by createWeek.
        const readableDate = formatChartDate(newDate);
        if (setChartDate) {
          console.log(`[handleDateChange] Updating chart date in App title: ${readableDate}`);
          setChartDate(readableDate);
        }

        // Refresh available dates
        const updatedFiles = await listWeeklyFiles();
        const updatedDates = updatedFiles
          .map(file => {
            const date = parse(file.name.trim(), 'M-d-yy', new Date());
            return isValid(date) ? date : null;
          })
          .filter((date): date is Date => date !== null);
        setAvailableDates(updatedDates);

        // Skip the LoadDataEffect since we're manually loading here
        skipNextLoadDataEffect.current = true;
        // Pass the new spreadsheet ID directly to avoid stale state race condition
        await loadData('Sheet1', newSpreadsheetId.id);
      }
    } catch (err) {
      console.error('[handleDateChange] Error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      setSelectedSpreadsheet(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (row: number, col: number, value: string) => {
    if (!isSignedIn) return;
    
    // Check if this is the YouTube Link column or S3 URL column
    const columnName = data?.headers[col];
    if (columnName?.toLowerCase().includes('youtube link')) {
      setEditingUrl({ row, col, url: value });
      setEditingCell(null);
      setEditingS3Url(null);
    } else if (columnName?.toLowerCase() === 's3 url') {
      setEditingS3Url({ row, col, url: value });
      setEditingCell(null);
      setEditingUrl(null);
    } else {
      setEditingCell({ row, col, value });
      setEditingUrl(null);
      setEditingS3Url(null);
    }
  };

  // Handle Seek In column clicks to open SeekTimeDialog
  const handleSeekInClick = (row: number, col: number, value: string, header: string) => {
    if (!isSignedIn || !data) return;
    
    // Get the song and artist for this row
    const songColIndex = data.headers.findIndex(h => 
      h?.toString().toLowerCase().includes('song') || 
      h?.toString().toLowerCase().includes('title'));
    const artistColIndex = data.headers.findIndex(h => 
      h?.toString().toLowerCase().includes('artist'));
    const s3UrlColIndex = data.headers.findIndex(h => 
      h?.toString().toLowerCase().includes('s3'));
    
    if (songColIndex === -1 || artistColIndex === -1) {
      console.warn('[handleSeekInClick] Could not find song or artist columns');
      return;
    }
    
    const rowData = data.rows[row];
    const songTitle = String(rowData[songColIndex] ?? '').trim();
    const artistName = String(rowData[artistColIndex] ?? '').trim();
    const s3Url = String(rowData[s3UrlColIndex] ?? '').trim();
    const currentSeekTime = parseFloat(value) || 0;
    
    if (!songTitle || !artistName) {
      console.warn('[handleSeekInClick] Missing song or artist data');
      return;
    }
    
    if (!s3Url) {
      console.warn('[handleSeekInClick] Missing S3 URL for video');
      return;
    }
    
    // Set up the SeekTimeDialog data
    setSeekTimeDialogData({
      row,
      col,
      videoUrl: s3Url,
      songTitle: `${songTitle} - ${artistName}`,
      artistName,
      currentSeekTime
    });
    setSeekTimeDialogOpen(true);
  };

  // Handle saving seek time changes to both weekly and master sheets
  const handleSeekTimeSave = async (newSeekTime: number) => {
    if (!seekTimeDialogData || !data) return;

    try {
      console.log(`[handleSeekTimeSave] Saving seek time: ${newSeekTime}s`);

      // Seek In is a property of the underlying video (Master Video List), keyed
      // by song + artist. Write it there so it follows the video everywhere.
      const songColIndex = data.headers.findIndex(h =>
        h?.toString().toLowerCase().includes('song') ||
        h?.toString().toLowerCase().includes('title'));
      const artistColIndex = data.headers.findIndex(h =>
        h?.toString().toLowerCase().includes('artist'));
      const songTitle = songColIndex !== -1 ? String(data.rows[seekTimeDialogData.row][songColIndex] ?? '').trim() : '';
      const artistName = artistColIndex !== -1 ? String(data.rows[seekTimeDialogData.row][artistColIndex] ?? '').trim() : '';

      if (songTitle && artistName) {
        await chartDataService.updateVideoByKey(songTitle, artistName, { seekIn: newSeekTime.toString() });
      } else {
        console.warn('[handleSeekTimeSave] Missing song/artist for this row; cannot save seek time');
      }

      // Update local grid data for immediate feedback
      const newData = { ...data };
      newData.rows[seekTimeDialogData.row][seekTimeDialogData.col] = newSeekTime.toString();
      setData(newData);

      console.log(`[handleSeekTimeSave] Successfully updated seek time to ${newSeekTime}s`);
    } catch (error) {
      console.error('[handleSeekTimeSave] Error saving seek time:', error);
    }
  };

  const handleCellEdit = async (row: number, col: number, value: string) => {
    if (!data || !selectedDate || !selectedSpreadsheet) {
      console.error('Missing required data for cell edit:', { data, selectedDate, selectedSpreadsheet });
      setError('Unable to update cell: missing required data');
      return;
    }
    
    try {
      // Get the current value of the cell
      const currentValue = data.rows[row][col];

      // Only update if the value has changed
      if (value !== currentValue) {
        // Map the grid cell (row, col) to a DB position + field and write it.
        // On a song/artist change the server re-links the position's video_id
        // automatically (no separate Master Video List rename needed).
        const mapping = COLUMN_FIELD_MAP[col];
        if (!mapping) {
          console.log(`[handleCellEdit] Column ${col} is not an editable position field; skipping`);
          return;
        }
        const position = positionForRowIndex(row);

        console.log('Attempting to update cell with:', { position, field: mapping.field, value, spreadsheetId: selectedSpreadsheet });
        await chartDataService.setPositionField(selectedSpreadsheet, position, mapping.field, value);

        // Add to change history (row/col drive undo/redo; no A1 range needed)
        setChangeHistory(prev => [...prev, {
          row,
          col,
          oldValue: currentValue,
          newValue: value,
          range: ''
        }]);

        // Clear redo history when a new change is made
        setRedoHistory([]);

        // Reload data to reflect changes
        console.log('Update successful, reloading data...');
        const updatedData = await loadData('Sheet1');
        if (!updatedData) {
          throw new Error('Failed to reload data after successful update');
        }

        // Clear any existing error
        setError(null);
      } else {
        console.log('Value unchanged, skipping update');
      }
    } catch (error) {
      console.error('Error in handleCellEdit:', error);
      
      // Extract the most meaningful error message
      let errorMessage = 'Failed to update cell';
      if (error instanceof Error) {
        errorMessage = error.message.replace(/^Failed to update cell: /, '');
        if (!errorMessage.includes('update') && !errorMessage.includes('cell')) {
          errorMessage = `Failed to update cell: ${errorMessage}`;
        }
      } else {
        errorMessage = `Failed to update cell: ${String(error)}`;
      }
      
      setError(errorMessage);
    } finally {
      setEditingCell(null);
    }
  };

  const handleUrlSave = async (url: string) => {
    if (!editingUrl || !data || !selectedDate || !selectedSpreadsheet) return;

    const { row, col } = editingUrl;
    const mapping = COLUMN_FIELD_MAP[col];
    if (!mapping) {
      console.log(`[handleUrlSave] Column ${col} is not an editable field; skipping`);
      setEditingUrl(null);
      return;
    }

    try {
      await chartDataService.setPositionField(selectedSpreadsheet, positionForRowIndex(row), mapping.field, url);
      await loadData('Sheet1');
      setEditingUrl(null);
    } catch (error) {
      console.error('Error updating URL:', error);
      if (error instanceof Error) {
        setError(`Failed to update URL: ${error.message}`);
      } else {
        setError('Failed to update URL. Please try again.');
      }
    }
  };

  const handleS3UrlSave = async (url: string) => {
    if (!editingS3Url || !data || !selectedDate || !selectedSpreadsheet) return;

    const { row, col } = editingS3Url;
    const mapping = COLUMN_FIELD_MAP[col];
    if (!mapping) {
      console.log(`[handleS3UrlSave] Column ${col} is not an editable field; skipping`);
      setEditingS3Url(null);
      return;
    }

    try {
      await chartDataService.setPositionField(selectedSpreadsheet, positionForRowIndex(row), mapping.field, url);
      await loadData('Sheet1');
      setEditingS3Url(null);
    } catch (error) {
      console.error('Error updating S3 URL:', error);
      if (error instanceof Error) {
        setError(`Failed to update S3 URL: ${error.message}`);
      } else {
        setError('Failed to update S3 URL. Please try again.');
      }
    }
  };

  const handleSignIn = async () => {
    try {
      // Auth is handled by the app's AuthContext (service-account backend);
      // there is no separate Google sign-in step. Treat the user as signed in.
      setIsSignedIn(true);

      const files = await listWeeklyFiles();
      
      if (files.length > 0) {
        const dates = files
          .map(file => {
            const date = parse(file.name.trim(), 'M-d-yy', new Date());
            return isValid(date) ? { date, id: file.id } : null;
          })
          .filter((item): item is { date: Date; id: string } => item !== null)
          .sort((a, b) => b.date.getTime() - a.date.getTime()); // Sort by date descending

        setAvailableDates(dates.map(item => item.date));
        
        // Always select the most recent date
        const mostRecent = dates[0];
        if (mostRecent) {
          setSelectedSpreadsheet(mostRecent.id);
          onDateChange(mostRecent.date);
          await loadData('Sheet1');
        }
      }
    } catch (err) {
      console.error('Error signing in:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    }
  };

  const handleCreateSheet = async (date: Date, _prefillPreviousWeek: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const newSheetName = getFormattedDate(date);

      // Create the week server-side. createWeek always carries over the previous
      // week's settings + song positions (and inserts no VO lines), which is the
      // relational equivalent of the old "duplicate previous week + clear VO"
      // prefill, so _prefillPreviousWeek no longer drives a separate code path.
      const created = await chartDataService.createWeek(chartCategory, newSheetName, formatChartDate(date));
      const newSpreadsheetId = created.id;

      const files = await listWeeklyFiles();
      const dates = files
        .map(file => {
          const date = parse(file.name.trim(), 'M-d-yy', new Date());
          return isValid(date) ? date : null;
        })
        .filter((date): date is Date => date !== null);

      setAvailableDates(dates);
      setSelectedSpreadsheet(newSpreadsheetId);
      onDateChange(date);

      // Set the chart date directly BEFORE loading data (the week's date_string
      // was set by createWeek). Prevents a flash of a stale date.
      const readableDate = formatChartDate(date);
      if (setChartDate) {
        console.log(`[handleCreateSheet] Setting chart date to: ${readableDate}`);
        setChartDate(readableDate);
      }

      await loadData('Sheet1');

    } catch (err) {
      console.error('Error creating sheet:', err);
      setError(err instanceof Error ? err.message : 'Failed to create sheet');
    } finally {
      setLoading(false);
    }
  };

  const shouldDisableDate = useCallback((date: Date) => {
    const formattedDateToCheck = getFormattedDate(date);
    
    // Disable all non-Saturday dates
    if (!isSaturday(date)) return true;
    
    // For Saturdays, check if they exist in availableDates
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDateToCheck
    );
    return false; // Don't disable Saturdays, we'll handle them in the renderer
  }, [availableDates, getFormattedDate]);

  // Populate the videoData map (autocomplete + row thumbnails) from the Master
  // Video List (the `videos` table).
  const loadVideoData = async () => {
    try {
      console.log('[loadVideoData] Fetching Master Video List');
      const videos = await chartDataService.listVideos();

      if (!videos.length) {
        console.error('No videos found in Master Video List');
        return;
      }

      const newVideoData: { [key: string]: VideoManagerData } = {};
      const uniqueArtists = new Set<string>();

      videos.forEach(v => {
        const songName = (v.song || '').trim();
        const artistName = (v.artist || '').trim();

        if (songName && artistName) {
          const key = normalizeSongArtistKey(songName, artistName);
          console.log(`[loadVideoData] Storing with key: "${key}" for "${songName} - ${artistName}"`);

          newVideoData[key] = {
            song: songName,
            artist: artistName,
            youtubeLink: (v.youtubeLink || '').trim(),
          };
          uniqueArtists.add(artistName);
        }
      });

      setVideoData(newVideoData);
      setSongArtistSuggestions(Object.keys(newVideoData).map(key => {
          const data = newVideoData[key];
          return `${data.song} - ${data.artist}`;
      }).sort());
      setArtistSuggestions(Array.from(uniqueArtists).sort());
      
      console.log('[loadVideoData] Updated videoData from Video Manager:', Object.keys(newVideoData).length, 'entries');
      // Log all keys for debugging
      console.log('[loadVideoData] All normalized keys:', Object.keys(newVideoData));
    } catch (error) {
      console.error('[loadVideoData] Error loading video data:', error);
    }
  };

  // Update the useEffect for loading video data
  // Load video data when the component is initialized and authenticated
  useEffect(() => {
    if (isSignedIn) {
      console.log('[SpreadsheetViewer] User is signed in, loading video data...');
      loadVideoData();
    }
  }, [isSignedIn]); // Only depend on authentication state

  // Add a separate effect to reload video data when the selected spreadsheet changes
  useEffect(() => {
    if (isSignedIn && selectedSpreadsheet) {
      console.log('[SpreadsheetViewer] Selected spreadsheet changed, ensuring video data is loaded');
      // Check if videoData is already loaded
      if (Object.keys(videoData).length === 0) {
        console.log('[SpreadsheetViewer] Video data not loaded yet, loading now');
        loadVideoData();
      }
    }
  }, [isSignedIn, selectedSpreadsheet]); // Remove videoData to avoid circular dependency

  // Add undo function
  const handleUndo = async () => {
    if (changeHistory.length === 0 || !selectedSpreadsheet) return;

    try {
      setLoading(true);
      setError(null);

      // Get the last change
      const lastChange = changeHistory[changeHistory.length - 1];

      // Revert the change (write the old value back to its DB position field)
      const mapping = COLUMN_FIELD_MAP[lastChange.col];
      if (mapping) {
        await chartDataService.setPositionField(
          selectedSpreadsheet,
          positionForRowIndex(lastChange.row),
          mapping.field,
          lastChange.oldValue
        );
      }

      // Move the change to redo history
      setRedoHistory(prev => [...prev, lastChange]);

      // Remove the last change from change history
      setChangeHistory(prev => prev.slice(0, -1));

      // Reload the data to reflect the change
      await loadData('Sheet1');

      setError(null);
    } catch (error) {
      console.error('Error in undo:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to undo: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Add redo function
  const handleRedo = async () => {
    if (redoHistory.length === 0 || !selectedSpreadsheet) return;

    try {
      setLoading(true);
      setError(null);

      // Get the last undone change
      const lastUndone = redoHistory[redoHistory.length - 1];

      // Re-apply the change (write the new value to its DB position field)
      const mapping = COLUMN_FIELD_MAP[lastUndone.col];
      if (mapping) {
        await chartDataService.setPositionField(
          selectedSpreadsheet,
          positionForRowIndex(lastUndone.row),
          mapping.field,
          lastUndone.newValue
        );
      }

      // Move the change back to change history
      setChangeHistory(prev => [...prev, lastUndone]);

      // Remove from redo history
      setRedoHistory(prev => prev.slice(0, -1));

      // Reload the data to reflect the change
      await loadData('Sheet1');

      setError(null);
    } catch (error) {
      console.error('Error in redo:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to redo: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Write the editable POSITION fields (song/artist/lastWeek/peak/weeks/message)
  // of one chart position from a source grid-row's values. Video framing fields
  // are intentionally NOT written here — the server re-links each position's
  // video_id by matching the new song/artist, so framing follows automatically.
  const POSITION_FIELD_COLUMNS: Array<{ col: number; field: string }> = [
    { col: 1, field: 'song' },
    { col: 2, field: 'artist' },
    { col: 3, field: 'lastWeek' },
    { col: 4, field: 'peak' },
    { col: 5, field: 'weeks' },
    { col: 10, field: 'message' },
  ];
  // Build a bulk-write item { position, fields } from a grid source row.
  const positionItemFromRow = (position: number, sourceRow: string[]): { position: number; fields: Record<string, string> } => {
    const fields: Record<string, string> = {};
    for (const { col, field } of POSITION_FIELD_COLUMNS) {
      fields[field] = String(sourceRow[col] ?? '');
    }
    return { position, fields };
  };

  const handleReorderSave = async (newOrder: number[]) => {
    if (!data || !selectedSpreadsheet) return;

    // newOrder[i] is the source row index that should occupy visual slot i
    // (top→bottom). Visual slot i corresponds to position (10 - i).
    // One atomic bulk write (all rows written, then re-linked) instead of an
    // N+1 storm of per-field PUTs — and it avoids transient swap mis-links.
    try {
      const items: Array<{ position: number; fields: Record<string, string> }> = [];
      for (let index = 0; index < newOrder.length; index++) {
        const sourceRow = data.rows[newOrder[index]];
        if (!sourceRow) continue;
        items.push(positionItemFromRow(10 - index, sourceRow));
      }
      if (items.length > 0) {
        await chartDataService.setPositionsBulk(selectedSpreadsheet, items);
      }
      // Reload the data to reflect changes
      await loadData('Sheet1');
    } catch (error) {
      console.error('Error updating order:', error);
      setError('Failed to update song order');
    }
  };

  const handleMoveSong = async (rowIndex: number, direction: 'up' | 'down') => {
    if (!data || !selectedSpreadsheet) return;
    
    // Find position column to understand current position
    const positionColumnIndex = data.headers.findIndex(header => 
      header.toLowerCase() === 'position'
    );
    
    if (positionColumnIndex === -1) return;
    
    // Get current position number from the data
    const currentPosition = parseInt(data.rows[rowIndex][positionColumnIndex], 10);
    
    // Calculate target position based on visual arrow direction and chart sort
    let targetPosition: number;
    
    if (sortAscending) {
      // Chart displays 1→10 (1 at top, 10 at bottom)
      if (direction === 'up') {
        // Up arrow = move visually up = lower position number
        targetPosition = currentPosition - 1;
      } else {
        // Down arrow = move visually down = higher position number
        targetPosition = currentPosition + 1;
      }
    } else {
      // Chart displays 10→1 (10 at top, 1 at bottom)  
      if (direction === 'up') {
        // Up arrow = move visually up = higher position number
        targetPosition = currentPosition + 1;
      } else {
        // Down arrow = move visually down = lower position number
        targetPosition = currentPosition - 1;
      }
    }
    
    // Validate target position (positions are 1-10)
    if (targetPosition < 1 || targetPosition > 10) return;
    
    // Find the row with the target position
    const targetRowIndex = data.rows.findIndex(row => 
      parseInt(row[positionColumnIndex], 10) === targetPosition
    );
    
    if (targetRowIndex === -1) return;
    
    try {

      // Get the two rows to swap (excluding position column)
      const currentRow = [...data.rows[rowIndex]];
      const targetRow = [...data.rows[targetRowIndex]];

      // Create swapped rows preserving position values
      const newCurrentRow = [...currentRow];
      const newTargetRow = [...targetRow];

      // Swap all columns except Position (column A)
      for (let i = 0; i < currentRow.length; i++) {
        if (i !== positionColumnIndex) {
          newCurrentRow[i] = targetRow[i];
          newTargetRow[i] = currentRow[i];
        }
      }

      console.log('Position-based row swap:', {
        currentPosition,
        targetPosition,
        currentRowIndex: rowIndex,
        targetRowIndex,
        direction
      });

      // Write the swapped content to each DB position slot (position numbers stay
      // fixed; the song/artist/etc. move). One atomic bulk write so both rows are
      // final before either is re-linked — avoids a transient swap mis-link.
      await chartDataService.setPositionsBulk(selectedSpreadsheet, [
        positionItemFromRow(currentPosition, newCurrentRow),
        positionItemFromRow(targetPosition, newTargetRow),
      ]);

      // Update local state with swapped rows
      const newRows = [...data.rows];
      newRows[rowIndex] = newCurrentRow;
      newRows[targetRowIndex] = newTargetRow;

      setData({ ...data, rows: newRows });

    } catch (error) {
      console.error('Error swapping song positions:', error);
      if (error instanceof Error) {
        setError(`Failed to swap song positions: ${error.message}`);
      } else {
        setError('Failed to swap song positions. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    // Reset all editing states
    setEditingCell(null);
    setEditingUrl(null);
    setEditingS3Url(null);
    
    // Clear the row data if we're editing a row
    if (data && editingCell) {
      const newRows = [...data.rows];
      newRows[editingCell.row] = newRows[editingCell.row].map(() => '');
      setData({ ...data, rows: newRows });
    }
  };

  const handleChartUpload = async (chartText: string) => {
    console.log('[handleChartUpload] Received chart text:', chartText);
    try {
      setLoading(true);
      // Store the chart text in case it's needed later by handleNewVideosSave
      setUploadedChartText(chartText); 
      
      // Process the chart text to get entries
      const chartEntries = await processChartText(chartText);
      console.log('[handleChartUpload] Processed entries:', chartEntries);

      // Call updateChartWithEntries directly
      // This function now fetches its own fresh video data.
      await updateChartWithEntries(chartEntries);
      
      setUploadChartDialogOpen(false); // Close the initial upload dialog
      setError(null);
    } catch (error) {
      console.error('Error processing chart upload:', error);
      setError('Failed to process chart upload. Please check the format or try again.');
      // Maybe don't close dialog on error? setUploadChartDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const getLastWeeksChartData = async (): Promise<{ [key: string]: { position: number; weeks: number } }> => {
    if (!selectedDate) return {};
    
    try {
      const lastWeekDate = subWeeks(selectedDate, 1);
      const lastWeekName = getFormattedDate(lastWeekDate);
      const files = await listWeeklyFiles();
      const lastWeekSpreadsheet = files.find(s => s.name === lastWeekName);

      if (!lastWeekSpreadsheet) return {};

      const positions = await chartDataService.getPositions(lastWeekSpreadsheet.id);
      if (!positions.length) return {};

      const result: { [key: string]: { position: number; weeks: number } } = {};

      positions.forEach(p => {
        const song = (p.song || '').trim();
        const artist = (p.artist || '').trim();
        const position = p.position;
        const weeks = parseInt(String(p.weeks || ''), 10) || 0;

        if (song && artist && !isNaN(position)) {
          const key = `${song} - ${artist}`.toLowerCase();
          result[key] = { position, weeks };
        }
      });

      return result;
    } catch (error) {
      console.error('Error getting last week\'s chart data:', error);
      return {};
    }
  };

  // Modify function signature to accept the up-to-date video data map
  const updateChartWithEntries = async (
    entries: { position: number; lastWeek: number | string; song: string; artist: string; peak?: number; weeks?: number; message?: string }[] // Added message to type
  ) => {
    if (!selectedSpreadsheet || !data) return;

    console.log('[updateChartWithEntries] Received entries:', JSON.stringify(entries, null, 2)); // <-- Log 4

    try {
      // --- Validate entries first ---
      console.log('[updateChartWithEntries] Validating entries...');
      const missingFields: string[] = [];

      entries.forEach((entry, index) => {
        const position = index + 1; // Expected position (1-based)
        const errors: string[] = [];

        if (entry.position === undefined || entry.position === null || isNaN(entry.position)) {
          errors.push('This Week position');
        }
        if (entry.lastWeek === undefined || entry.lastWeek === null) {
          errors.push('Last Week position');
        }
        if (!entry.song || entry.song.trim() === '') {
          errors.push('Song name');
        }
        if (!entry.artist || entry.artist.trim() === '') {
          errors.push('Artist name');
        }
        if (entry.peak === undefined || entry.peak === null || isNaN(entry.peak)) {
          errors.push('Peak position');
        }
        if (entry.weeks === undefined || entry.weeks === null || isNaN(entry.weeks)) {
          errors.push('Weeks on chart');
        }

        if (errors.length > 0) {
          missingFields.push(`Position ${position}: Missing ${errors.join(', ')}`);
        }
      });

      if (missingFields.length > 0) {
        const errorMessage = `Chart data is incomplete:\n${missingFields.join('\n')}`;
        console.error('[updateChartWithEntries] Validation failed:', errorMessage);
        setError(errorMessage);
        throw new Error(errorMessage);
      }

      console.log('[updateChartWithEntries] Validation passed - all entries have required fields');

      // --- Fetch fresh video data inside the function ---
      console.log('[updateChartWithEntries] Fetching fresh Master Video List...');
      const freshVideos = await chartDataService.listVideos();
      const freshVideoDataMap: { [key: string]: VideoManagerData } = {};
      if (freshVideos.length) {
        freshVideos.forEach(v => {
          const song = (v.song || '').trim();
          const artist = (v.artist || '').trim();
          if (song && artist) {
            const key = normalizeSongArtistKey(song, artist);
            console.log(`[updateChartWithEntries MapBuild] Adding key: "${key}" for ${song} - ${artist}`);
            freshVideoDataMap[key] = {
              song,
              artist,
              youtubeLink: (v.youtubeLink || '').trim(),
            };
          }
        });
        console.log(`[updateChartWithEntries] Built fresh map with ${Object.keys(freshVideoDataMap).length} entries.`);

        // Update videoData state with the fresh map
        setVideoData(prevVideoData => ({
          ...prevVideoData,
          ...freshVideoDataMap
        }));
        console.log('[updateChartWithEntries] Merged freshVideoDataMap into videoData state.');
      } else {
        console.log('[updateChartWithEntries] Master Video List is empty.');
      }
       // --- End fetching fresh video data ---

      // --- Log all keys found in the fresh map ---
      console.log('[updateChartWithEntries] Keys in freshVideoDataMap:', Object.keys(freshVideoDataMap));

      // Get last week's chart data
      const lastWeekData = await getLastWeeksChartData();
      
      // Get current chart data state (for isNewEntry check - might still have timing issues)
      const currentEntries = new Set<string>();
      const currentSongIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
      const currentArtistIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));
      
      if (currentSongIndex !== -1 && currentArtistIndex !== -1) {
        data.rows.forEach(row => {
          const song = String(row[currentSongIndex] || '').trim();
          const artist = String(row[currentArtistIndex] || '').trim();
          if (song && artist) {
            currentEntries.add(normalizeSongArtistKey(song, artist)); 
          }
        });
      }
      
      // Write each entry to its DB position slot (1..10). Song/artist drive the
      // server-side video re-link, so the YouTube link / framing follow from the
      // matched video and are not written here. We write song last so the final
      // re-link (which matches on the position's current song AND artist) sees
      // both updated values.
      // Build one atomic bulk write for all imported positions. The server writes
      // every field, THEN re-links each position to its video — so the old
      // "artist/song last" per-field ordering is no longer needed here.
      const bulkItems: Array<{ position: number; fields: Record<string, string> }> = [];
      for (const entry of entries) {
        const position = entry.position;
        if (!(position >= 1 && position <= 10)) {
          console.warn(`[updateChartWithEntries] Skipping entry with out-of-range position ${position}`);
          continue;
        }

        // Use normalized key for lookup against the Master Video List for the
        // canonical song/artist spelling.
        const key = normalizeSongArtistKey(entry.song, entry.artist);
        const videoInfo = freshVideoDataMap[key];
        console.log(`[updateChartWithEntries] Processing Entry (Pos ${position}):`, entry, 'videoInfo:', videoInfo);

        const artistName = addLineBreakDelimiter(videoInfo?.artist || entry.artist);
        const songName = addLineBreakDelimiter(videoInfo?.song || entry.song);

        const fields: Record<string, string> = {
          lastWeek: entry.lastWeek === 0 ? '-' : entry.lastWeek.toString(),
          message: entry.message || '', // always set, even if empty
          artist: artistName,
          song: songName,
        };
        if (entry.weeks !== undefined) fields.weeks = entry.weeks.toString();
        if (entry.peak !== undefined) fields.peak = entry.peak.toString();
        bulkItems.push({ position, fields });
      }

      if (bulkItems.length > 0) {
        await chartDataService.setPositionsBulk(selectedSpreadsheet, bulkItems);
      }

      // Reload to reflect the imported rankings
      await loadData('Sheet1');
    } catch (error) {
      console.error('Error updating chart:', error);
      throw new Error('Failed to update chart with new data');
    }
  };

  // Build a song+artist → video map from the Master Video List.
  const fetchAndBuildVideoDataMap = async (): Promise<Map<string, VideoManagerData>> => {
    console.log('[fetchAndBuildVideoDataMap] Fetching Master Video List...');
    const map = new Map<string, VideoManagerData>();
    try {
      const videos = await chartDataService.listVideos();
      videos.forEach(v => {
        const song = (v.song || '').trim();
        const artist = (v.artist || '').trim();
        const youtubeLink = (v.youtubeLink || '').trim();
        if (song && artist) {
          const key = normalizeSongArtistKey(song, artist);
          map.set(key, { song, artist, youtubeLink });
        }
      });
      console.log(`[fetchAndBuildVideoDataMap] Built map with ${map.size} entries.`);
    } catch (error) {
      console.error('[fetchAndBuildVideoDataMap] Error fetching or building map:', error);
      // Optionally re-throw or handle error appropriately
    }
    return map;
  };

  // handleNewVideosSave signature needs to accept chartText
  const handleNewVideosSave = async (videos: Video[], chartText: string): Promise<void> => { // Explicitly type the return
    console.log('[handleNewVideosSave ENTRY] Function called. Videos received:', JSON.stringify(videos, null, 2));
    console.log('[handleNewVideosSave ENTRY] Received chartText length:', chartText?.length);

    // --- IMMEDIATELY trigger downloads for videos with YouTube links ---
    // This creates import jobs right away so Import Manager has data to show
    const videosWithLinks = videos.filter(v => v.youtubeLink && v.youtubeLink.trim());

    // Deduplicate by YouTube URL to prevent importing the same video twice
    const seenUrls = new Set<string>();
    const uniqueVideosWithLinks = videosWithLinks.filter(v => {
      const normalizedUrl = v.youtubeLink!.trim().toLowerCase();
      if (seenUrls.has(normalizedUrl)) {
        console.log(`[handleNewVideosSave] Skipping duplicate YouTube URL for: ${v.artist} - ${v.song}`);
        return false;
      }
      seenUrls.add(normalizedUrl);
      return true;
    });

    // IMPORTANT: Skip immediate download trigger when there's only 1 video
    // because onComplete will navigate to UploadVideo which handles the download itself.
    // Only trigger downloads here for multiple videos (which go to Import Manager instead).
    if (uniqueVideosWithLinks.length === 1) {
      console.log(`[handleNewVideosSave] Single video detected - skipping immediate download (UploadVideo will handle it)`);
    } else if (uniqueVideosWithLinks.length > 1) {
      console.log(`[handleNewVideosSave] IMMEDIATELY triggering downloads for ${uniqueVideosWithLinks.length} videos (${videosWithLinks.length - uniqueVideosWithLinks.length} duplicates skipped)...`);
      for (const video of uniqueVideosWithLinks) {
        const videoId = crypto.randomUUID();
        console.log(`[handleNewVideosSave] Starting download for: ${video.artist} - ${video.song}`);

        // Fire off download request without waiting
        fetch(`${SERVER_BASE_URL}/api/download-youtube`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            videoId,
            youtubeUrl: video.youtubeLink,
            song: video.song,
            artist: video.artist
          })
        })
          .then(response => {
            if (!response.ok) {
              console.error(`[handleNewVideosSave] Failed to start download for ${video.artist} - ${video.song}`);
            } else {
              console.log(`[handleNewVideosSave] Download queued successfully for ${video.artist} - ${video.song}`);
            }
          })
          .catch(error => {
            console.error(`[handleNewVideosSave] Error triggering download for ${video.artist} - ${video.song}:`, error);
          });
      }
    }

    try {
      // --- Simplified Fetching and Updating Logic ---
      console.log('[handleNewVideosSave] Fetching fresh video data from sheet...');
      // *** CALL THE NEW HELPER FUNCTION ***
      const freshVideoDataMap = await fetchAndBuildVideoDataMap();

      console.log(`[handleNewVideosSave] Found ${Object.keys(freshVideoDataMap).length} existing keys in sheet (Map size: ${freshVideoDataMap.size}).`); // Log Map size

      const videosToActuallyAdd: Video[] = [];
      const videosToUpdateLocally: Video[] = []; // Track videos that exist but might need local YT link update

      videos.forEach(video => {
        const key = normalizeSongArtistKey(video.song, video.artist);
        if (!freshVideoDataMap.has(key)) {
          console.log(`[handleNewVideosSave Filter] Key "${key}" NOT in sheet. Will add.`);
          videosToActuallyAdd.push(video);
        } else {
          console.log(`[handleNewVideosSave Filter] Key "${key}" EXISTS in sheet. Will update locally if needed.`);
          // Even if it exists, we might have added a YouTube link in the dialog
          videosToUpdateLocally.push(video);
        }
      });

      // --- Add to Master Video List ---
      if (videosToActuallyAdd.length > 0) {
        console.log(`[handleNewVideosSave] Adding ${videosToActuallyAdd.length} new videos to the Master Video List.`);
        for (const video of videosToActuallyAdd) {
          // Upserts on (song, artist); safe even if a concurrent add already created it.
          await chartDataService.createVideo({
            song: video.song,
            artist: video.artist,
            youtubeLink: video.youtubeLink || '',
          });
        }
        console.log('[handleNewVideosSave] Added new videos to Master Video List.');
      } else {
        console.log('[handleNewVideosSave] No new videos to add to Master Video List.');
      }

      // --- Update Local State (Both new and existing potentially updated) --- 
      console.log('[handleNewVideosSave] Updating local videoData state...');
      setVideoData(prevVideoData => {
          const updatedData = { ...prevVideoData };
          // Add newly added videos
          videosToActuallyAdd.forEach(video => {
              const key = normalizeSongArtistKey(video.song, video.artist);
              updatedData[key] = {
                  song: video.song,
                  artist: video.artist,
                  youtubeLink: video.youtubeLink || ''
              };
          });
          // Update potentially changed existing videos (e.g., added youtubeLink)
          videosToUpdateLocally.forEach(video => {
              const key = normalizeSongArtistKey(video.song, video.artist);
              // Ensure we don't overwrite existing link with empty if dialog didn't provide one
              const existingLink = updatedData[key]?.youtubeLink;
              updatedData[key] = {
                  song: video.song,
                  artist: video.artist,
                  youtubeLink: video.youtubeLink || existingLink || '' // Prioritize new link, then existing, then empty
              };
          });
          return updatedData;
      });
      console.log('[handleNewVideosSave] Local videoData state updated.');

      // Note: Downloads are triggered at the START of this function (before sheet operations)
      // so Import Manager can show them immediately

      // --- Process Chart --- 
      if (!chartText) {
        console.warn('[handleNewVideosSave] chartText is empty or undefined. Skipping chart processing.');
      } else {
        console.log('[handleNewVideosSave] Processing chart text parameter...');
        const chartEntries = await processChartText(chartText);
        console.log('[handleNewVideosSave] Passing these chartEntries to updateChartWithEntries:', JSON.stringify(chartEntries, null, 2));
        await updateChartWithEntries(chartEntries); // This function now uses the latest videoData state implicitly
      }

      console.log('[handleNewVideosSave SUCCESS] Function finished successfully.');

    } catch (error) {
      console.error('[handleNewVideosSave CATCH] Error saving new videos:', error);
      setError('Failed to save new videos. Please try again.');
    } finally {
       console.log('[handleNewVideosSave FINALLY] Add new video process complete.');
       // Dialog closing is handled by ChartUploadDialog now
    }
  };

  const handleVideoSelectedForChart = async (videoId: string) => {
    if (searchTargetRowIndex === null) return;
    
    console.log(`[SpreadsheetViewer] Video selected for chart row ${searchTargetRowIndex}, ID: ${videoId}`);
    setLoading(true);
    setError(null);

    try {
      const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;

      // 1. Fetch official info
      console.log(`[SpreadsheetViewer] Fetching official info for ${fullUrl}`);
      const officialSongInfo = await youtubeService.getSongInfo(fullUrl);
      console.log(`[SpreadsheetViewer] Official info:`, officialSongInfo);

      const newVideoKey = normalizeSongArtistKey(officialSongInfo.song, officialSongInfo.artist);

      // 2. Upsert into the Master Video List (sets/refreshes the YouTube link,
      // keyed by song + artist).
      try {
        await chartDataService.updateVideoByKey(officialSongInfo.song, officialSongInfo.artist, { youtubeLink: fullUrl });
        console.log(`[SpreadsheetViewer] Upserted video "${newVideoKey}" with YouTube link.`);

        // Immediate local videoData state update
        const newEntryForVideoData: VideoManagerData = {
          song: officialSongInfo.song,
          artist: officialSongInfo.artist,
          youtubeLink: fullUrl,
        };
        setVideoData(prev => ({ ...prev, [newVideoKey]: newEntryForVideoData }));
        loadVideoData(); // Reload in background just in case
      } catch (vmError) {
        console.error('[SpreadsheetViewer] Error upserting video into Master Video List:', vmError);
      }

      // 3. Update the current chart position's song/artist. The server re-links
      // the position's video_id by matching song + artist, so the YouTube link
      // (now on the video) follows automatically.
      if (!selectedSpreadsheet) {
        throw new Error('Current spreadsheet ID is missing.');
      }
      const position = positionForRowIndex(searchTargetRowIndex);
      console.log(`[SpreadsheetViewer] Updating chart position ${position} (row ${searchTargetRowIndex})...`);

      await chartDataService.setPositionField(selectedSpreadsheet, position, 'artist', officialSongInfo.artist);
      await chartDataService.setPositionField(selectedSpreadsheet, position, 'song', officialSongInfo.song);
      console.log(`[SpreadsheetViewer] Current chart position updated.`);

      // Reload data to reflect the new song/artist + linked video
      loadData('Sheet1'); // No need to await this for UI responsiveness

    } catch (error) {
      console.error('[SpreadsheetViewer] Error in handleVideoSelectedForChart:', error);
      setError(error instanceof Error ? error.message : 'Failed to add video and update chart.');
    } finally {
      setLoading(false);
      setSearchDialogOpen(false);
      setSearchTargetRowIndex(null);
      setSearchInitialQuery('');
    }
  };

  // Add a loading tracker ref to prevent concurrent initializations
  const isInitializing = useRef(false);
  // Track if initial load has completed - once true, never re-run full initialization
  const hasCompletedInitialLoad = useRef(false);
  // Track when we've manually loaded data (e.g., from handleDateChange) to skip redundant useEffect loads
  const skipNextLoadDataEffect = useRef(false);
  const previousFolderId = useRef(folderId);

  // Reset initialization when chart category changes (folderId changes)
  useEffect(() => {
    if (previousFolderId.current && previousFolderId.current !== folderId) {
      console.log('[initializeApp] folderId changed, resetting initialization flags');
      hasCompletedInitialLoad.current = false;
      isInitializing.current = false;
    }
    previousFolderId.current = folderId;
  }, [folderId]);

  // Update the useEffect for initial loading to prevent duplicate initialization
  useEffect(() => {
    const initializeApp = async () => {
      // Wait for context to finish discovering folders
      if (isContextLoading) {
        console.log('[initializeApp] Waiting for context to finish loading...');
        return;
      }

      // Don't proceed without a valid folder ID
      if (!folderId) {
        console.log('[initializeApp] No folder ID available, waiting for context discovery...');
        return;
      }

      // Prevent concurrent initializations
      if (isInitializing.current) {
        console.log('[initializeApp] Initialization already in progress, skipping duplicate call');
        return;
      }

      // Once initial load is complete, never re-run initialization
      // This prevents the bug where changing sheets triggers initializeApp (because loadData changes)
      // which then auto-selects the old sheet and overwrites the user's selection
      if (hasCompletedInitialLoad.current) {
        console.log('[initializeApp] Initial load already completed, skipping re-initialization');
        return;
      }

      isInitializing.current = true;
      console.log('[initializeApp] Starting app initialization');
      
      try {
        setIsInitialized(true);

        // Auth is handled by the app's AuthContext (service-account backend);
        // reaching this component means the user is authenticated.
        setIsSignedIn(true);

        // Load all available dates (do this only once)
        console.log('[initializeApp] Fetching weeks for chart category:', chartCategory);
        
        // Cache the file list response to avoid redundant calls
          const files = await listWeeklyFiles();
          console.log('Found files:', files.map(f => f.name));
          
        // Parse dates from filenames
          const dates = files
            .map(file => {
              const date = parse(file.name.trim(), 'M-d-yy', new Date());
              console.log('Parsing date from filename:', file.name, '→', date);
            return isValid(date) ? date : null;
          })
          .filter((date): date is Date => date !== null);
          
        setAvailableDates(dates);
        console.log('Parsed dates:', dates.map(d => getFormattedDate(d)));
        
        // Check for previously selected date
        const savedSpreadsheetId = localStorage.getItem(`billboard_currentSpreadsheetId_${chartCategory}`);
        let shouldAutoSelect = true;
        
        if (savedSpreadsheetId) {
          // Find the corresponding date
          const matchingFile = files.find(file => file.id === savedSpreadsheetId);
          if (matchingFile) {
            const date = parse(matchingFile.name.trim(), 'M-d-yy', new Date());
            if (isValid(date)) {
              console.log('Initializing with existing selected date:', matchingFile.name);
              setSelectedSpreadsheet(savedSpreadsheetId);
              onDateChange(date);
              
              // Important: Only load data once the component is fully initialized
              // Use setTimeout to break potential dependency cycles and clear isInitializing after load
              setTimeout(async () => {
                await loadData('Sheet1');
                isInitializing.current = false;
                hasCompletedInitialLoad.current = true;
              }, 0);
              shouldAutoSelect = false; // Don't auto-select since we found a valid saved selection
            } else {
              // Invalid date, continue to auto-selection
              console.log('Saved spreadsheet has invalid date format, continuing to auto-selection');
            }
          } else {
            // Saved ID not found, clear it and continue to auto-selection
            console.log('Saved spreadsheet ID not found, clearing localStorage');
            localStorage.removeItem(`billboard_currentSpreadsheetId_${chartCategory}`);
          }
        }
        
        if (shouldAutoSelect && dates.length > 0) {
          // No saved selection, auto-select the most recent date
          const mostRecentDate = dates.sort((a, b) => b.getTime() - a.getTime())[0];
          const mostRecentFile = files.find(file => {
            const fileDate = parse(file.name.trim(), 'M-d-yy', new Date());
            return isValid(fileDate) && fileDate.getTime() === mostRecentDate.getTime();
          });
          
          if (mostRecentFile) {
            console.log('Auto-selecting most recent date:', mostRecentFile.name);
            setSelectedSpreadsheet(mostRecentFile.id);
            onDateChange(mostRecentDate);
            
            // Save the selection to localStorage
            localStorage.setItem(`billboard_currentSpreadsheetId_${chartCategory}`, mostRecentFile.id);
            
            // Important: Only load data once the component is fully initialized
            // Use setTimeout to break potential dependency cycles and clear isInitializing after load
            setTimeout(async () => {
              await loadData('Sheet1');
              isInitializing.current = false;
              hasCompletedInitialLoad.current = true;
            }, 0);
          } else {
            isInitializing.current = false;
            hasCompletedInitialLoad.current = true;
          }
        } else {
          // No dates found, clear initialization flag
          console.log('No valid date files found');
          isInitializing.current = false;
          hasCompletedInitialLoad.current = true;
        }
      } catch (err) {
        console.error('[initializeApp] Error initializing app:', err);
        setError('Failed to initialize the application');
        isInitializing.current = false;
      }
    };

    initializeApp();
  }, [folderId, isContextLoading, onDateChange, loadData, getFormattedDate]); // Add missing dependencies

  // USE EFFECT: Load data when selected spreadsheet changes (and user is signed in)
  useEffect(() => {
    // Skip if we've already manually loaded data (e.g., from handleDateChange)
    // This prevents the stale closure issue where this effect runs with old spreadsheet ID
    if (skipNextLoadDataEffect.current) {
      console.log(`[LoadDataEffect] Skipping - data was already loaded manually`);
      skipNextLoadDataEffect.current = false;
      return;
    }

    // Only run if initialization is complete, user is signed in, and a sheet is selected
    if (isInitialized && isSignedIn && selectedSpreadsheet && !isInitializing.current) {
      console.log(`[LoadDataEffect] Selected spreadsheet changed to ${selectedSpreadsheet}. Loading data...`);
      // loadData now handles sync internally before updating the UI
      loadData('Sheet1');
    } else {
        console.log(`[LoadDataEffect] Skipping load. isInitialized: ${isInitialized}, isSignedIn: ${isSignedIn}, selectedSpreadsheet: ${selectedSpreadsheet}, isInitializing: ${isInitializing.current}`);
    }
  }, [selectedSpreadsheet, isInitialized, isSignedIn, loadData]);

  // Add a new handler for the render button - now uses weekly sheet directly
  const handleRender = async () => {
    if (!selectedDate || !selectedSpreadsheet) {
      setError('Please select a valid date and sheet first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[Output] Using weekly sheet directly: ${selectedSpreadsheet}`);

      // Resolve the weekly sheet name for nav context. Prefer the already-loaded
      // weeks list (works in both Sheets and DB mode); fall back to getFileName
      // (Drive). For DB-created weeks (synthetic db_ ids) there's no Drive file, so
      // this must NOT throw — the name is only nav-state context.
      const weeklySheetName = await resolveWeeklySheetName(selectedSpreadsheet);

      // Navigate directly to Intro/Outro page with weekly sheet ID
      navigate(`/intro-outro/${selectedSpreadsheet}`, {
        state: { 
          sheetName: weeklySheetName,
          formattedDate: formatChartDate(selectedDate)
        }
      });

    } catch (err) {
      console.error('[IntroOutro] Error navigating to intro/outro: ', err);
      setError(err instanceof Error ? err.message : 'Failed to navigate to intro/outro page.');
    } finally {
      setLoading(false);
    }
  };
  // ---> END: Add handler for Intro/Outro Button

  // Add the missing generateAutocompleteOptions function
  const generateAutocompleteOptions = (data: SpreadsheetData): void => {
    if (!data) return;

    const songColIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex === -1 || artistColIndex === -1) return;
    
    const uniqueArtists = new Set<string>();
    const songArtistOptions: string[] = [];
    
    data.rows.forEach(row => {
      const song = String(row[songColIndex] || '').trim();
      const artist = String(row[artistColIndex] || '').trim();
      
      if (song && artist) {
        uniqueArtists.add(artist);
        songArtistOptions.push(`${song} - ${artist}`);
      }
    });
    
    setSongArtistSuggestions(songArtistOptions.sort());
    setArtistSuggestions(Array.from(uniqueArtists).sort());
  };

  const renderCell = (value: string, header: string, rowIndex: number, colIndex: number) => {
    const isPositionColumn = header.toLowerCase() === 'position';
    const isEditable = !isPositionColumn && !['youtube link', 'artwork url', 's3 url', 'voice over text', 'voice over url'].includes(header.toLowerCase());
    const isCenterAligned = ['last week', 'this week', 'peak', 'duration', 'in point', 'seek in'].some(term => header.toLowerCase().includes(term)) || header.toLowerCase() === 'weeks';
    const isNumeric = ['last week', 'this week', 'peak', 'duration'].some(term => header.toLowerCase().includes(term)) || header.toLowerCase() === 'weeks';
    const isThumbnail = header.toLowerCase() === 'thumbnail';
    const isVideoTitle = header.toLowerCase() === 'video title';
    const isArtist = header.toLowerCase() === 'artist';
    const isLabel = header.toLowerCase() === 'label';
    const isDuration = header.toLowerCase() === 'duration';
    const isInPoint = header.toLowerCase() === 'in point';
    const isZoom = header.toLowerCase() === 'zoom';
    const isVoiceOverText = header.toLowerCase() === 'voice over text';
    const isVoiceOverUrl = header.toLowerCase() === 'voice over url';
    const isArtworkUrl = header.toLowerCase() === 'artwork url';
    const isS3Url = header.toLowerCase() === 's3 url';
    const isMusicVideo = header.toLowerCase() === 'youtube link';
    const isSong = header.toLowerCase().includes('song');

    if (isPositionColumn) {
      return (
        <Typography variant="body2" sx={{
            textAlign: 'center',
            width: '60px',
            padding: '4px',
            cursor: 'default',
            userSelect: 'none',
          fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'transparent'
            }
        }}>
            {rowIndex + 1}
          </Typography>
      );
    }

    // --- Special handling for Last Week column to show '-' for 0 --- 
    const isLastWeekColumn = header.toLowerCase().includes('last week');
    if (isLastWeekColumn && editingCell?.row !== rowIndex && editingCell?.col !== colIndex) {
      const displayValue = (value === '0' || value === '') ? '-' : value;
      return (
        <Box sx={{ 
          display: 'block',
          width: '100%',
          pt: '4px',
          textAlign: 'center', // Center align Last Week
          cursor: isSignedIn ? 'pointer' : 'default' // Add pointer cursor to indicate it's editable
        }}>
          {displayValue}
        </Box>
      );
    }
    // --- End of Last Week handling ---

    if (editingCell?.row === rowIndex && editingCell?.col === colIndex) {
      if (isSong) {
        return (
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={2}
            value={delimiterToNewline(editingCell.value)}
            onChange={(e) => setEditingCell(prev => prev ? { ...prev, value: newlineToDelimiter(e.target.value) } : null)}
            onBlur={() => {
              if (editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            autoFocus
            size="small"
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': {
                padding: '4px 8px'
              }
            }}
          />
        );
      } else if (isArtist) {
        return (
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={2}
            value={delimiterToNewline(editingCell.value)}
            onChange={(e) => setEditingCell(prev => prev ? { ...prev, value: newlineToDelimiter(e.target.value) } : null)}
            onBlur={() => {
              if (editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
              setEditingCell(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            autoFocus
            size="small"
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': {
                padding: '4px 8px'
              }
            }}
          />
        );
      } else if (isZoom) {
        return (
          <Box sx={{ 
            display: 'flex !important', 
            justifyContent: 'center !important',
            width: '100% !important',
            height: '100% !important',
            alignItems: 'center !important',
            padding: '0 !important',
            margin: '0 !important',
            position: 'relative !important',
            top: '50% !important',
            transform: 'translateY(-50%) !important'
          }}>
            <Checkbox
              checked={value.toUpperCase() === 'TRUE'}
              onChange={(e) => handleCellEdit(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onClick={(e) => e.stopPropagation()}
              sx={{
                margin: '0 !important',
                padding: '0 !important',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem !important'  // Increased and made consistent size
                }
              }}
            />
          </Box>
        );
      } else {
        return (
          <input
            type="text"
            value={editingCell.value}
            onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
            onBlur={() => {
              if (editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
            }}
            autoFocus
            style={{ width: '100%' }}
          />
        );
      }
    } else if (isSong) {
      // Find the Voice Over Text column
      const voiceOverColIndex = data?.headers.findIndex(header => 
        header.toLowerCase().includes('voice over text')
      );
      const voiceOverText = typeof voiceOverColIndex === 'number' && voiceOverColIndex !== -1 && data?.rows && data.rows[rowIndex] ?
        data.rows[rowIndex][voiceOverColIndex] : '';

      // Check if this song or artist has a delimiter to adjust VO positioning
      const songParts = splitAtDelimiter(value);
      const artistColIndex = data?.headers.findIndex(h => h.toLowerCase().includes('artist'));
      const artistValue = typeof artistColIndex === 'number' && artistColIndex !== -1 && data?.rows && data.rows[rowIndex] ?
        data.rows[rowIndex][artistColIndex] : '';
      const artistParts = splitAtDelimiter(artistValue);
      const hasMultiLineText = !!songParts.line2 || !!artistParts.line2;

      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Box sx={{
            cursor: isSignedIn ? 'pointer' : 'default',
            color: value ? 'text.primary' : 'text.disabled',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          onClick={() => isSignedIn && setEditingCell({ row: rowIndex, col: colIndex, value })}
          >
            {songParts.line2 ? (
              <Box sx={{ lineHeight: 1.2 }}>
                <Box sx={{ whiteSpace: 'nowrap' }}>{songParts.line1}</Box>
                <Box sx={{ whiteSpace: 'nowrap' }}>{songParts.line2}</Box>
              </Box>
            ) : (
              <Box sx={{ whiteSpace: 'nowrap' }}>{songParts.line1}</Box>
            )}
          </Box>
          {editingCell?.row === rowIndex && editingCell?.col === voiceOverColIndex ? (
            <TextField
              value={editingCell.value}
              onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
              onBlur={() => {
                if (editingCell) {
                  handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && editingCell) {
                  handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                }
              }}
              autoFocus
              size="small"
              fullWidth
              placeholder={`${value} VO`}
              sx={{ 
                '& .MuiInputBase-root': {
                  padding: '0px',
                  fontSize: '0.875rem',
                  width: '200%',
                  position: 'relative',
                  left: '-50%'
                }
              }}
            />
          ) : (
            <Box 
              sx={{ 
                cursor: isSignedIn ? 'pointer' : 'default',
                color: voiceOverText ? 'text.secondary' : 'text.disabled',
                fontSize: '0.875rem',
                width: 'auto',
                minWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'unset',
                mt: hasMultiLineText ? 5.5 : 7.5, // Less margin if song has multiple lines to keep VO aligned with thumbnail bottom
                fontStyle: !voiceOverText ? 'italic' : 'normal',
                paddingRight: '0px',
                position: 'relative',
                display: 'block',
                zIndex: 1
              }}
              onClick={(e) => {
                e.stopPropagation(); // Keep stopPropagation
                if (isSignedIn) {
                  // Navigate to VO Manager with song/artist as query params
                  if (value) {
                    // Get the artist from the row data if possible
                    const artistColIndex = data?.headers.findIndex(h => h.toLowerCase().includes('artist'));
                    // Only access data.rows[rowIndex][artistColIndex] if all values are defined
                    let rowArtist = '';
                    if (artistColIndex !== undefined && artistColIndex !== -1 && data?.rows && data.rows[rowIndex]) {
                      rowArtist = data.rows[rowIndex][artistColIndex] || '';
                    }
                    // Keep this as VO Manager since it's specifically for voice over content
                    navigate(`/vo-manager?song=${encodeURIComponent(value)}&artist=${encodeURIComponent(rowArtist)}&play=true`);
                  }
                }
              }}
            >
              {voiceOverText || `${value} VO`}
            </Box>
          )}
        </Box>
      );
    } else if (isMusicVideo) {
      // Look up the song and artist for this row
      const songColumnIndex = data?.headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColumnIndex = data?.headers.findIndex(h => h.toLowerCase().includes('artist'));
      
      // Only proceed if we can find song and artist columns
      if (songColumnIndex !== -1 && artistColumnIndex !== -1 && data?.rows[rowIndex]) {
        const songName = data.rows[rowIndex][songColumnIndex as number] || '';
        const artistName = data.rows[rowIndex][artistColumnIndex as number] || '';
        
        const key = normalizeSongArtistKey(songName, artistName);
        console.log(`[renderCell] Row ${rowIndex}: Song="${songName}", Artist="${artistName}", Generated Key="${key}"`);
        
        // Try to find video info in our videoData map
        const videoInfo = videoData[key];
        console.log(`[renderCell] Found videoInfo for key "${key}":`, videoInfo);
        
        // Get YouTube ID either from the cell value or from videoData
        const videoId = getYouTubeVideoId(value) || (videoInfo?.youtubeLink ? getYouTubeVideoId(videoInfo.youtubeLink) : null);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {videoId ? (
              <img
                src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                alt="Video thumbnail"
                style={{
                  width: '120px',
                  height: '68px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (songName && artistName) {
                    // Set a flag in sessionStorage before navigating
                    sessionStorage.setItem('navigatedFromCharts', 'true');
                    // Navigate to Video Manager with song/artist params and player parameter
                    navigate(`/video-manager?song=${encodeURIComponent(songName)}&artist=${encodeURIComponent(artistName)}&player=true`);
                  } else if (videoInfo?.youtubeLink) {
                    window.open(videoInfo.youtubeLink, '_blank');
                  }
                }}
              />
            ) : (
              <IconButton 
                onClick={() => handleRequestVideoAdd(rowIndex)}
                sx={{ 
                  p: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            )}
          </Box>
        );
      }
      
      // Fallback if we can't find song/artist
      const videoIdFromCell = getYouTubeVideoId(value);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {videoIdFromCell ? (
            <img
              src={`https://img.youtube.com/vi/${videoIdFromCell}/mqdefault.jpg`}
              alt="Video thumbnail"
              style={{
                width: '120px',
                height: '68px',
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (value) {
                  window.open(value, '_blank');
                }
              }}
            />
          ) : (
            <IconButton 
              onClick={() => handleRequestVideoAdd(rowIndex)}
              sx={{ 
                p: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          )}
        </Box>
      );
    } else if (isVoiceOverText) {
      // Skip rendering Voice Over Text in its own column since it's now under the song
      return null;
    } else if (isZoom) {
      return (
          <Box sx={{ 
            display: 'flex !important', 
            justifyContent: 'center !important',
            width: '100% !important',
            height: '100% !important',
            alignItems: 'center !important',
            padding: '0 !important',
            margin: '0 !important',
            position: 'relative !important',
            top: '50% !important',
            transform: 'translateY(-50%) !important'
          }}>
            <Checkbox
              checked={value.toUpperCase() === 'TRUE'}
              onChange={(e) => handleCellEdit(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onClick={(e) => e.stopPropagation()}
              sx={{
                margin: '0 !important',
                padding: '0 !important',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem !important'  // Increased and made consistent size
                }
              }}
            />
          </Box>
      );
    } else {
      return (
        <Box sx={{
          display: 'block',
          width: '100%',
          pt: '4px',
          textAlign: isCenterAligned ? 'center' : 'left',
          cursor: (isSong || isArtist) && isSignedIn ? 'pointer' : 'default'
        }}
        onClick={
          (isSong || isArtist) && isSignedIn
            ? () => setEditingCell({ row: rowIndex, col: colIndex, value })
            : undefined
        }>
          {(isSong || isArtist) ? (
            (() => {
              const parts = splitAtDelimiter(value);
              return parts.line2 ? (
                <Box sx={{ lineHeight: 1.2 }}>
                  <Box>{parts.line1}</Box>
                  <Box>{parts.line2}</Box>
                </Box>
              ) : (
                parts.line1
              );
            })()
          ) : value}
        </Box>
      );
    }
  };

  // Restore the handleSongSelect function
  const handleSongSelect = async (rowIndex: number, songColIndex: number, selectedValue: string | null) => {
    if (!selectedValue || !data || !selectedSpreadsheet) {
      // If cleared or required data missing, clear the song + related position
      // fields (artist, last week, peak, weeks) for this row's position.
      if (data && selectedSpreadsheet) {
        const position = positionForRowIndex(rowIndex);
        try {
          // Clears song (and re-links video_id to none since song is now blank).
          await handleCellEdit(rowIndex, songColIndex, '');
          await chartDataService.setPositionField(selectedSpreadsheet, position, 'artist', '');
          await chartDataService.setPositionField(selectedSpreadsheet, position, 'lastWeek', '');
          await chartDataService.setPositionField(selectedSpreadsheet, position, 'peak', '');
          await chartDataService.setPositionField(selectedSpreadsheet, position, 'weeks', '');
          await loadData('Sheet1');
        } catch (error) {
          console.error('Error clearing related fields:', error);
          setError(error instanceof Error ? `Failed to clear related fields: ${error.message}` : 'Failed to clear related fields');
        }
      }
      setEditingCell(null); // Close editor
      return;
    }

    // 1. Parse Song and Artist from the selected "Song - Artist" string
    const parts = selectedValue.split(' - ');
    const songName = parts[0]?.trim();
    // Handle cases where artist might have '-' in their name
    const artistName = parts.slice(1).join(' - ').trim();

    if (!songName || !artistName) {
      console.warn('Could not parse song/artist from:', selectedValue);
      // Just save the raw selected value to the song cell if parsing fails
      await handleCellEdit(rowIndex, songColIndex, selectedValue);
      setEditingCell(null);
      return;
    }

    // 2. Create the lookup key
    const videoKey = normalizeSongArtistKey(songName, artistName);

    // 3. Find data in videoData state (for the canonical artist spelling)
    const match = videoData[videoKey];

    console.log(`[handleSongSelect] Row: ${rowIndex}, Selection: "${selectedValue}", Parsed Song: "${songName}", Parsed Artist: "${artistName}", Match found:`, match);

    // 4. Write song + artist to the position. The server re-links the video_id
    // by matching song + artist, so the YouTube link / framing follow the video.
    const position = positionForRowIndex(rowIndex);
    try {
      setLoading(true); // Indicate activity
      // Artist first, then song, so the final re-link sees both values.
      await chartDataService.setPositionField(selectedSpreadsheet, position, 'artist', match?.artist || artistName);
      await chartDataService.setPositionField(selectedSpreadsheet, position, 'song', songName);

      // Reload data to show changes
      console.log('[handleSongSelect] Updates successful, reloading data...');
      await loadData('Sheet1');
      setError(null);
    } catch (error) {
      console.error('Error in handleSongSelect update:', error);
      setError(error instanceof Error ? `Failed to update row: ${error.message}` : 'Failed to update row');
    } finally {
      setLoading(false);
      setEditingCell(null); // Close the editor
    }
  };

  // Restore the handleRequestVideoAdd function
  const handleRequestVideoAdd = (rowIndex: number) => {
    if (!data) return;

    const songColIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex !== -1 && artistColIndex !== -1) {
      const song = data.rows[rowIndex]?.[songColIndex] || '';
      const artist = data.rows[rowIndex]?.[artistColIndex] || '';
      const query = `${song} ${artist}`.trim();

      console.log(`[SpreadsheetViewer] Requesting video add for row ${rowIndex}: "${query}"`);
      setSearchInitialQuery(query);
      setSearchTargetRowIndex(rowIndex);
      setSearchDialogOpen(true);
    } else {
      console.error('[SpreadsheetViewer] Cannot find Song or Artist column index.');
      setError('Could not initiate video search: Song or Artist column missing.');
    }
  };

  // Logout handlers with confirmation dialog
  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // App auth is owned by AuthContext (service-account backend); clearing it
      // is the real sign-out. There is no separate Google session to end.
      logout(); // Clear AuthContext user info
      setIsSignedIn(false);
      // Clear any sensitive data
      setData(null);
      setSelectedSpreadsheet(null);
      setAvailableDates([]);
      setVideoData({});
      setLogoutDialogOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleOutputManager = async () => {
    if (selectedSpreadsheet) {
      localStorage.setItem(`billboard_currentSpreadsheetId_${chartCategory}`, selectedSpreadsheet);
    }
    navigate('/output-manager');
  };


  // --- NEW: Handler for INTRO/OUTRO button navigation - now uses weekly sheet directly ---
  const handleIntroOutroNavigation = async () => {
    if (!selectedDate || !selectedSpreadsheet) {
      setError('Please select a valid date and chart spreadsheet first.');
      enqueueSnackbar('Please select a valid date and chart spreadsheet first.', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[IntroOutroNav] Using weekly sheet directly: ${selectedSpreadsheet}`);
      // Resolve via the weeks list first (both modes); fall back to Drive. Must not
      // throw for DB-created weeks (no Drive file) — name is only nav context.
      const weeklySheetName = await resolveWeeklySheetName(selectedSpreadsheet);
      console.log(`[IntroOutroNav] Weekly sheet name: ${weeklySheetName}`);

      // Navigate directly to intro/outro with weekly sheet ID
      console.log(`[IntroOutroNav] Navigating to /intro-outro/${selectedSpreadsheet}`);
      const readableDate = formatChartDate(selectedDate!);
      navigate(`/intro-outro/${selectedSpreadsheet}`, {
          state: {
              sheetName: weeklySheetName,
              formattedDate: readableDate
          }
      });

    } catch (err) {
      console.error('[IntroOutroNav] Error navigating to intro/outro: ', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to navigate to intro/outro page.';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // --- END NEW HANDLER ---

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button variant="contained" onClick={handleSignIn}>
          Sign in with Google
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      mt: 0,
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Wrapper to align toolbar with table edges */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ display: 'inline-block' }}>
      <Box sx={{
        mb: 1,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between' // This pushes left and right groups apart
      }}>
        {/* Left Group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 0 } }}>
        <DatePicker
          label="Week Ending"
          value={selectedDate}
          onChange={handleDateChange}
          disabled={!isSignedIn}
          sx={{ 
            width: 180,
            '& .MuiInputLabel-root': {
              backgroundColor: 'white',
              padding: '0 4px',
              transform: 'translate(14px, 16px) scale(0.75)',
              '&.Mui-focused': {
                transform: 'translate(14px, 16px) scale(0.75)'
              },
              zIndex: 1,
              position: 'relative'
            },
            '& .MuiOutlinedInput-root': {
              position: 'relative',
              zIndex: 0,
              padding: '8px 14px',
              '& .MuiOutlinedInput-notchedOutline': {
                paddingRight: '0'
              }
            }
          }}
          shouldDisableDate={shouldDisableDate}
          slots={{
            day: CustomDay
          }}
          slotProps={{
            textField: {
              size: 'small'
            }
          }}
        />
          {/* Start of reordered button block */}
          <Button // Upload Chart
            variant="outlined"
          onClick={() => setUploadChartDialogOpen(true)}
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
              '&:hover': { 
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
            marginTop: '22px'
          }}
        >
          Upload Chart
        </Button>
          <Button // VO Manager
            variant="outlined"
          onClick={() => navigate('/vo-manager')}
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
              '&:hover': { 
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
            marginTop: '22px'
          }}
        >
          VO Manager
        </Button>
          {/* Add Intro/Outro Button here */}
          <Button // INTRO/OUTRO
            onClick={handleIntroOutroNavigation}
            variant="outlined"
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
            '&:hover': {
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
              marginTop: '22px'
            }}
          >
            Intro/Outro
        </Button>
        {/* Undo/Redo buttons - commented out but kept for future use
        <IconButton
          onClick={handleUndo}
          disabled={!isSignedIn || changeHistory.length === 0}
          size="small"
          title="Undo last change"
          sx={{ marginTop: '16px' }}
        >
          <UndoIcon />
        </IconButton>
        <IconButton
          onClick={handleRedo}
          disabled={!isSignedIn || redoHistory.length === 0}
          size="small"
          title="Redo last undone change"
          sx={{ marginTop: '16px' }}
        >
          <RedoIcon />
        </IconButton>
        */}
          {/* Video Manager Button removed from here */}
        </Box>

        {/* Right-aligned group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 0 } }}>
           {/* ... Login Indicator, Logout Icon, OUTPUT MANAGER Button ... */}
        {/* Commented out for service account auth - will add back for user login later
        <Box sx={{ marginTop: '16px' }}>
          <LoginIndicator />
        </Box>
        <IconButton
          onClick={handleLogout}
          disabled={!isSignedIn}
          size="small"
          title="Sign out"
            sx={{ marginTop: '16px' }} // Keep vertical alignment consistent
        >
          <LogoutIcon />
        </IconButton>
        */}
          {/* Video Manager Button and OUTPUT MANAGER button are swapped back here */}
          <Button // Video Manager (moved back before OUTPUT MANAGER)
            variant="contained"
            onClick={() => navigate('/video-manager')}
            disabled={!isSignedIn}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              marginTop: '22px',
              backgroundColor: '#000000',
              color: themeColor,
              '&:hover': {
                backgroundColor: '#1A1A1A',
                color: themeColor,
              },
            }}
          >
            Video Manager
          </Button>
          <Button // Import Manager
            variant="contained"
            onClick={() => navigate('/import-manager')}
            disabled={!isSignedIn}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginTop: '22px',
              backgroundColor: '#000000',
              color: themeColor,
              '&:hover': {
                backgroundColor: '#1A1A1A',
                color: themeColor,
              },
            }}
          >
            Import Manager
            {importManagerNotifications > 0 && (
              <Box
                sx={{
                  backgroundColor: '#f44336',
                  color: '#ffffff',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                {importManagerNotifications}
              </Box>
            )}
          </Button>
          <Button // OUTPUT MANAGER (moved back after Video Manager)
            variant="contained"
            onClick={handleOutputManager}
            disabled={!isSignedIn || loading}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginTop: '22px', // Keep existing margin for alignment
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: themeColor,
              color: '#000000',
              '&:hover': {
                backgroundColor: themeHoverColor,
                color: '#000000',
              },
            }}
          >
            OUTPUT MANAGER
            {outputManagerNotifications > 0 && (
              <Box
                sx={{
                  backgroundColor: themeColor,
                  border: '2px solid #000000',
                  color: '#000000',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                {outputManagerNotifications}
              </Box>
            )}
          </Button>
        </Box>
      </Box>

      <CreateSheetDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateSheet={handleCreateSheet}
        existingDates={availableDates}
        handleCancel={handleCancel}
      />

      <UrlDialog
        open={editingUrl !== null}
        onClose={() => setEditingUrl(null)}
        url={editingUrl?.url || ''}
        onSave={handleUrlSave}
      />

      <UrlDialog
        open={editingS3Url !== null}
        onClose={() => setEditingS3Url(null)}
        url={editingS3Url?.url || ''}
        onSave={handleS3UrlSave}
        title="Edit S3 URL"
      />

      <ChartUploadDialog
        open={uploadChartDialogOpen}
        onClose={() => setUploadChartDialogOpen(false)}
        onUpload={handleChartUpload}
        videoData={videoData}
        // Update the wrapper function to accept and pass chartText with types
        onDetailsSave={async (videosFromDialog: Video[], textFromDialog: string) => {
          console.log('[SpreadsheetViewer onDetailsSave PROP] Triggered! Text length:', textFromDialog?.length);
          await handleNewVideosSave(videosFromDialog, textFromDialog);
        }}
        // Navigate based on number of new videos
        onComplete={(missingCount, videos) => {
          if (missingCount === 0) {
            // No new videos - stay on charts page (do nothing)
            console.log('[ChartUpload] No new videos, staying on charts page');
          } else if (missingCount === 1 && videos && videos[0]) {
            // One new video - go to upload video page
            const video = videos[0];
            const params = new URLSearchParams({
              song: video.song,
              artist: video.artist,
            });
            if (video.youtubeLink) {
              params.set('youtubeUrl', video.youtubeLink);
            }
            console.log('[ChartUpload] One new video, navigating to upload-video');
            navigate(`/upload-video?${params.toString()}`);
          } else {
            // Multiple new videos - go to import manager
            console.log('[ChartUpload] Multiple new videos, navigating to import-manager');
            navigate('/import-manager');
          }
        }}
      />

      {/* Add the YouTube Search Dialog */}
      <YouTubeSearchDialog
         open={searchDialogOpen}
         onClose={() => {
             setSearchDialogOpen(false);
             setSearchTargetRowIndex(null);
             setSearchInitialQuery('');
         }}
         onSelect={handleVideoSelectedForChart}
         initialQuery={searchInitialQuery}
      />

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutCancel}>
        <DialogTitle>Log Out</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleLogoutCancel}
            sx={{ color: '#000000' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              backgroundColor: themeColor,
              color: '#000000',
              '&:hover': {
                backgroundColor: themeHoverColor,
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {error ? (
        <Alert severity="error" sx={{ mt: 1, mx: 0.5 }}>{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : data && data.headers && data.headers.length > 0 && data.rows && data.rows.length > 0 ? (
        // Debug: Log render state to help track the intermittent chart display issue
        console.log('[SpreadsheetViewer] Rendering chart - data available:', !!data, 'headers:', data?.headers?.length, 'rows:', data?.rows?.length),
        <TableContainer 
          component={Paper}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            '& .MuiTable-root': {
              width: 'fit-content',
              tableLayout: 'auto',
              display: 'table'
            },
            '& .MuiTableBody-root': {
              display: 'table-row-group'
            },
            '& .MuiTableRow-root': {
              display: 'table-row',
              height: '106px'  // Consistent height for all rows
            },
            '& .MuiTableCell-root': {
              display: 'table-cell',
              px: 0.5,
              py: 0.5,
              fontSize: '0.875rem',
              verticalAlign: 'top',
              paddingTop: '16px',  // Consistent top padding
              '&:first-of-type, &:last-of-type': {
                px: 0.5
              }
            },
            '& .sticky-column': {
              position: 'sticky',
              zIndex: 1,
              '&.thumbnail': { 
                left: '160px', 
                width: '187px', 
                padding: '16px 0',
                borderLeft: 'none',
                borderRight: 'none'
              },
              '&.position': { left: 0, width: '160px' }
            },
            '& .non-sticky-columns': {
              paddingLeft: '347px'
            }
          }}
        >
          <Table sx={{ width: 'fit-content', tableLayout: 'auto', display: 'table' }} aria-label="spreadsheet data">
            <TableHead sx={{ 
              bgcolor: 'black',
              '& .MuiTableRow-root': {
                height: 'auto'  // Changed from a fixed 32px to auto
              },
              '& .MuiTableCell-root': {
                fontWeight: '900 !important',
                fontSize: '1.25rem !important',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
                padding: '4px 6px !important',
                height: '32px !important',
                lineHeight: '1.2 !important',
                color: 'white !important',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&.song-header': {
                  paddingLeft: '24px !important',
                  paddingRight: '6px !important'
                }
              }
            }}>
              <TableRow>
                {/* Position Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: 0,
                    width: '160px',
                    zIndex: 2,
                    borderRight: 'none',
                    textAlign: 'center',
                    fontSize: '1.25rem',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    cursor: 'pointer', // Add cursor pointer to indicate it's clickable
                    userSelect: 'none', // Prevent text selection
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className="sticky-column position"
                  onClick={toggleSortDirection} // Add click handler to toggle sort direction
                >
                  Position
                  {sortAscending ? ' ↑' : ' ↓'} {/* Show sort direction indicator */}
                </TableCell>

                {/* Reorder Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: '160px',
                    width: '60px',
                    zIndex: 2,
                    borderLeft: 'none',
                    borderRight: 'none',
                    textAlign: 'center',
                    fontSize: '1.25rem',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                  className="sticky-column reorder"
                >
                </TableCell>

                {/* Thumbnail Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: '220px',
                    width: '187px',
                    zIndex: 2,
                    borderLeft: 'none',
                    borderRight: 'none',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                  className="sticky-column thumbnail"
                >
                </TableCell>

                {/* Rest of the columns */}
                {data.headers // <-- Keep iterating over data.headers
                  // REMOVE the .filter() call here
                  .map((header, index) => { // <-- MAP DIRECTLY
                    // ---> REMOVE CONSOLE LOG HERE (if it exists) <--- 
                    // console.log("[TableHead] Processing header:", header);

                    // Ensure we skip any potentially remaining empty headers after loadData
                    if (header.trim() === '') return null; 

                    // Skip columns that should definitely not have a header cell rendered here
                    const lowerHeader = header.toLowerCase().trim();
                    if (
                      lowerHeader === 'position' ||
                      lowerHeader.includes('youtube link') ||
                      lowerHeader.includes('artwork url') ||
                      lowerHeader === 's3 url' ||
                      lowerHeader === 'voice over text' ||
                      lowerHeader === 'voice over url' ||
                      lowerHeader === 'vo in' || // Keep the correct names here just in case
                      lowerHeader === 'vo out' ||  // Keep the correct names here just in case
                      lowerHeader.includes('line vo') || // Hide all Line VO columns (Line VO, Line VO db, Line VO URL, Line VO In/Out, etc.)
                      lowerHeader.includes('uploaded vo') || // Hide Uploaded VO column(s)
                      lowerHeader === 'zoom' || // Also hide zoom column
                      lowerHeader === 'duration' || // Hide duration column
                      lowerHeader === 'vertical url' || // Hide Vertical URL column
                      lowerHeader === 'adjustment' || // Hide Adjustment column
                      lowerHeader === 'in point' // Hide In Point column
                    ) {
                      return null;
                    }

                    const shouldCenterAlign = header.toLowerCase().includes('last week') ||
                        header.toLowerCase().includes('this week') ||
                        header.toLowerCase().includes('peak') ||
                        header.toLowerCase() === 'weeks' ||
                        header.toLowerCase().includes('seek in');
                    
                    return (
                      <TableCell 
                        key={header + '-' + index} // Use header and index for a more robust key
                        align={shouldCenterAlign ? "center" : "left"}
                        sx={{
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                          padding: '8px 16px',
                          color: 'white',
                          bgcolor: 'black',
                          height: '48px',
                          lineHeight: '1.5',
                          fontSize: '1.25rem',
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          ...(header.toLowerCase().includes('last week') && {
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px'
                          }),
                          ...(header.toLowerCase().includes('peak') && {
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px'
                          }),
                          ...(header.toLowerCase() === 'weeks' && {
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px'
                          }),
                          ...(header.toLowerCase() === 'message' && {
                            width: 'auto',
                            minWidth: 'fit-content',
                            paddingRight: '20px !important'
                          })
                        }}
                        className={header.toLowerCase().includes('song') ? 'song-header' : ''}
                      >
                        {header}
                      </TableCell>
                    );
                  })}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Generate sorted rows based on position value and current sort direction */}
              {(() => {
                // First, extract and filter valid rows
                const validRows = data.rows
                  .map((row, idx) => ({ row, idx }))
                  .filter(({ row }) => {
                    const posIndex = data.headers.findIndex(h => h.toLowerCase() === 'position');
                    if (posIndex === -1) return false;
                    const pos = parseInt(row[posIndex], 10);
                    return !isNaN(pos) && pos >= 1 && pos <= 10;
                  });

                // Sort rows based on position value and current sort direction
                const sortedRows = [...validRows].sort((a, b) => {
                  const posIndex = data.headers.findIndex(h => h.toLowerCase() === 'position');
                  const posA = parseInt(a.row[posIndex], 10);
                  const posB = parseInt(b.row[posIndex], 10);
                  
                  // If ascending (1-10), use posA - posB; if descending (10-1), use posB - posA
                  return sortAscending ? posA - posB : posB - posA;
                });

                // Return the sorted rows as JSX
                return sortedRows.map(({ row, idx: rowIndex }) => {
                // Find the Position column index IN THE ORIGINAL headers
                const originalPositionIndex = data.headers.findIndex(header => 
                  header.toLowerCase() === 'position'
                );
                
                  // const position = parseInt(row[originalPositionIndex], 10); // Removed unused

                // Find the Song and Artist column indices IN THE ORIGINAL headers
                const originalSongColumnIndex = data.headers.findIndex(header => 
                  header.toLowerCase().includes('song')
                );
                const originalArtistColumnIndex = data.headers.findIndex(header => 
                  header.toLowerCase().includes('artist')
                );
                
                const songName = originalSongColumnIndex !== -1 ? String(row[originalSongColumnIndex] || '').trim() : '';
                const artistName = originalArtistColumnIndex !== -1 ? String(row[originalArtistColumnIndex] || '').trim() : '';
                const videoKey = normalizeSongArtistKey(songName, artistName); // Use consistent normalization
                
                  // Add debug logging to the main table rendering section for thumbnails
                console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Song="${songName}", Artist="${artistName}", Generated Key="${videoKey}"`);
                const videoInfo = videoData[videoKey];
                  // Log the videoData keys to help debugging
                  console.log(`[SpreadsheetViewer Render] videoData contains ${Object.keys(videoData).length} keys.`);
                console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Lookup result for key "${videoKey}":`, videoInfo);

                const videoId = videoInfo?.youtubeLink ? getYouTubeVideoId(videoInfo.youtubeLink) : null;
                  console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Video ID: ${videoId}`);
                
                // --- Get indices of VISIBLE columns for rendering ---
                const visibleHeaders = data.headers.filter(header => {
                    const lowerHeader = header.toLowerCase().trim(); // Trim whitespace
                    return !(
                      lowerHeader === '' || // <-- Add check for empty string
                      lowerHeader === 'position' ||
                      lowerHeader.includes('youtube link') ||
                      lowerHeader.includes('artwork url') ||
                      lowerHeader === 's3 url' ||
                      lowerHeader === 'voice over text' ||
                      lowerHeader === 'voice over url' ||
                      lowerHeader === 'voice over in' ||
                        lowerHeader === 'voice over out' ||
                        lowerHeader.includes('line vo') || // Hide all Line VO columns (Line VO, Line VO db, Line VO URL, Line VO In/Out, etc.)
                        lowerHeader.includes('uploaded vo') || // Hide Uploaded VO column(s)
                        lowerHeader === 'vo in' || // Consistent with TableHead filter
                        lowerHeader === 'vo out' || // Consistent with TableHead filter
                        lowerHeader === 'zoom' || // Also hide zoom column
                        lowerHeader === 'duration' || // Hide duration column
                        lowerHeader === 'vertical url' || // Hide Vertical URL column
                        lowerHeader === 'adjustment' || // Hide Adjustment column
                        lowerHeader === 'in point' // Hide In Point column
                    );
                  });

                return (
                  <TableRow key={`${rowIndex}-main`}
                    sx={{
                      bgcolor: rowIndex % 2 === 0 ? 'background.paper' : 'rgba(0, 0, 0, 0.02)',
                      '& .sticky-column': {
                        bgcolor: 'inherit !important'
                      },
                      '& td': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    {/* Position Column (uses original index for data access) */}
                    <TableCell
                      onClick={() => {
                        // Remove navigation, make it non-interactive
                      }}
                      sx={{ 
                        position: 'sticky',
                        left: 0,
                        width: '160px',
                        height: '70px', // Adjusted height? Check consistency
                        zIndex: 1,
                        borderRight: 'none',
                        textAlign: 'center',
                        padding: '8px 0 0 0',
                        bgcolor: 'inherit',
                        cursor: 'default', // Change to default cursor
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center'
                      }}
                      className="sticky-column position"
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontWeight: 900,
                          fontSize: {
                            xs: '4rem',
                            sm: '5rem',
                            md: '6rem'
                          },
                          lineHeight: 1,
                          width: '100%',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'black'
                        }}
                      >
                        {row[originalPositionIndex]} {/* Use original index */}
                      </Typography>
                    </TableCell>

                    {/* Reorder Column */}
                    <TableCell
                      sx={{ 
                        position: 'sticky',
                        left: '160px',
                        width: '60px',
                        height: '70px', // Check height consistency
                        zIndex: 1,
                        borderLeft: 'none',
                        borderRight: 'none',
                        textAlign: 'center',
                        padding: '8px 0 0 0',
                        bgcolor: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      className="sticky-column reorder"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSong(rowIndex, 'up')}
                        disabled={rowIndex === 0}
                        sx={{ 
                          padding: '4px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSong(rowIndex, 'down')}
                        disabled={rowIndex === data.rows.length - 1} // Needs adjustment if rows are filtered
                        sx={{ 
                          padding: '4px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </TableCell>

                    {/* Thumbnail / Add Video Column */}
                    <TableCell
                      className="sticky-column thumbnail"
                      sx={{
                        position: 'sticky',
                        left: '220px', 
                        width: '187px',
                        height: '106px',
                        zIndex: 1,
                        borderLeft: 'none',
                        borderRight: 'none',
                        padding: '0', 
                        bgcolor: 'inherit'
                      }}
                    >
                      {videoId ? (
                        <img
                          src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                          alt="Video thumbnail"
                          style={{
                            width: '187px',
                            height: '106px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'block' 
                          }}
                          onClick={() => {
                              if (songName && artistName) {
                                // Navigate to Video Manager with song/artist params and player parameter
                                // Don't pass S3 URL - let Video Manager look it up from master sheet
                                const queryParams = new URLSearchParams();
                                queryParams.append('song', songName);
                                queryParams.append('artist', artistName);
                                queryParams.append('player', 'true');
                                navigate(`/video-manager?${queryParams.toString()}`);
                              } else if (videoInfo?.youtubeLink) {
                               window.open(videoInfo.youtubeLink, '_blank');
                             }
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                         }}>
                          <IconButton 
                             onClick={() => handleRequestVideoAdd(rowIndex)}
                             title="Add YouTube Video Link"
                             sx={{ 
                                 color: 'rgba(0, 0, 0, 0.54)'
                             }}
                          >
                            <AddCircleOutlineIcon sx={{ fontSize: 40 }} /> 
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>

                    {/* Rest of the columns */}
                    {visibleHeaders.map((header, visibleIndex) => { // <-- MAP OVER FILTERED HEADERS
                      // Find the original index corresponding to this visible header
                      const originalIndex = data.headers.findIndex(h => h === header); 
                      if (originalIndex === -1) return null; // Should not happen if filtering is correct

                      const cellValue = row[originalIndex]; // Use original index to get cell value

                      const shouldCenterAlign = header.toLowerCase().includes('last week') ||
                        header.toLowerCase().includes('this week') ||
                        header.toLowerCase().includes('peak') ||
                        header.toLowerCase() === 'weeks' ||
                        header.toLowerCase().includes('in point') ||
                        header.toLowerCase().includes('seek in') ||
                        header.toLowerCase().includes('duration');
                      
                      return (
                        <TableCell
                          key={header + '-' + visibleIndex} // Use visible index for key
                          onClick={() => {
                            const lowerHeader = header.toLowerCase();
                            if (lowerHeader === 'seek in' || lowerHeader === 'in point') {
                              // Handle Seek In column clicks
                              handleSeekInClick(rowIndex, originalIndex, cellValue, header);
                            } else {
                              handleCellClick(rowIndex, originalIndex, cellValue);
                            }
                          }}
                          align={shouldCenterAlign ? "center" : "left"}
                          sx={{ 
                            cursor: isSignedIn ? 'pointer' : 'default',
                            whiteSpace: 'nowrap',
                            padding: '16px 24px 0 24px',
                            height: '106px',
                            bgcolor: 'inherit',
                            display: 'table-cell',
                            verticalAlign: 'top',
                            ...(header.toLowerCase().includes('song') && {
                              paddingLeft: '24px !important'
                            }),
                            ...(header.toLowerCase().includes('last week') && {
                              width: '120px',
                              minWidth: '120px',
                              maxWidth: '120px'
                            }),
                            ...(header.toLowerCase().includes('peak') && {
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px'
                            }),
                            ...(header.toLowerCase() === 'weeks' && {
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px'
                            }),
                            ...(header.toLowerCase() === 'message' && {
                              width: 'auto',
                              minWidth: 'fit-content',
                              paddingRight: '20px !important'
                            })
                          }}
                        >
                          {/* Pass ORIGINAL index to renderCell */}
                          {renderCell(cellValue, header, rowIndex, originalIndex)} 
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Fallback for when data exists but is invalid
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, mb: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {data ? 'Chart data is loading...' : 'No chart data available. Please select a date or sign in.'}
          </Typography>
        </Box>
      )}
      </Box>{/* End inline-block wrapper */}
      </Box>{/* End centering wrapper */}

      {/* SeekTimeDialog for Charts page */}
      {seekTimeDialogOpen && seekTimeDialogData && (
        <SeekTimeDialog
          open={seekTimeDialogOpen}
          onClose={(newSeekTime: number) => {
            if (newSeekTime !== seekTimeDialogData.currentSeekTime) {
              handleSeekTimeSave(newSeekTime);
            }
            setSeekTimeDialogOpen(false);
            setSeekTimeDialogData(null);
          }}
          videoUrl={seekTimeDialogData.videoUrl}
          videoTitle={seekTimeDialogData.songTitle}
        />
      )}
    </Box>
  );
};

export default SpreadsheetViewer;